import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ShieldCheck, Users as UsersIcon, Film, Calendar, Ticket, Megaphone,
  Layers, Languages as LangIcon, Settings, Bell, Loader2, Search,
  RefreshCw, CheckCircle2, Clock3, Trash2,
  Plus, RadioTower, SlidersHorizontal, PoundSterling,
  Copy, UserCog, MapPin, BarChart3, Sparkles, CreditCard,
  Mail, Phone, MessageCircle, Instagram, Inbox, Eye, X, Pencil, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import KnowledgeDirectory from "@/components/KnowledgeDirectory";
import { adminKnowledgeSections } from "@/content/helpContent";

const statusStyles = {
  confirmed: "bg-emerald-500/12 text-emerald-600 border-emerald-500/20",
  scanned: "bg-sky-500/12 text-sky-600 border-sky-500/20",
  cancelled: "bg-muted text-muted-foreground border-border",
  refunded: "bg-rose-500/12 text-rose-600 border-rose-500/20",
  partially_refunded: "bg-amber-500/12 text-amber-700 border-amber-500/20",
  approved: "bg-emerald-500/12 text-emerald-600 border-emerald-500/20",
  pending: "bg-amber-500/12 text-amber-700 border-amber-500/20",
  rejected: "bg-rose-500/12 text-rose-600 border-rose-500/20",
  new: "bg-sky-500/12 text-sky-700 border-sky-500/20",
  contacted: "bg-amber-500/12 text-amber-700 border-amber-500/20",
  permission_granted: "bg-emerald-500/12 text-emerald-600 border-emerald-500/20",
  event_created: "bg-primary/10 text-primary border-primary/20",
  closed: "bg-muted text-muted-foreground border-border",
  active: "bg-emerald-500/12 text-emerald-600 border-emerald-500/20",
  inactive: "bg-muted text-muted-foreground border-border",
};
const ticketingLabels = {
  namaste: "Namaste ticketing",
  external: "External ticket link",
  inquiry: "Inquiry only",
};
const paymentModeLabels = {
  disabled: "Payments disabled",
  platform: "Namaste collects",
  stripe_connect: "Stripe Express",
};
const asArray = (value) => Array.isArray(value) ? value : [];

export default function AdminPanel() {
  const { user, checkAuth } = useAuth();
  const [keyInput, setKeyInput] = useState("");
  const [authing, setAuthing] = useState(false);

  if (!user || user.role !== "admin") {
    const submit = async () => {
      setAuthing(true);
      try {
        await api.post("/admin/login", { key: keyInput });
        await checkAuth();
        toast.success("Welcome, admin");
      } catch {
        toast.error("Invalid admin key");
      } finally {
        setAuthing(false);
      }
    };

    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="mx-auto max-w-md px-5 py-16">
          <div className="rounded-lg border border-border p-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <h1 className="font-display text-2xl font-extrabold">Admin Panel</h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {user ? "Enter the admin web key to elevate this account." : "Sign in first, then enter the admin web key."}
            </p>
            <Input
              data-testid="admin-key-input"
              type="password"
              placeholder="Admin Web Key"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              className="mt-4"
            />
            <Button data-testid="admin-key-submit" disabled={authing || !user} onClick={submit} className="mt-3 w-full rounded-full">
              {authing ? "Verifying..." : "Authenticate"}
            </Button>
            {!user && (
              <Button variant="outline" onClick={() => { window.location.href = "/login"; }} className="mt-2 w-full rounded-full">
                Sign in first
              </Button>
            )}
          </div>
        </main>
      </div>
    );
  }

  return <AdminConsole />;
}

function AdminConsole() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") || "overview";
  const focusedEventId = searchParams.get("event") || "";
  const [data, setData] = useState({
    stats: null,
    movies: [],
    events: [],
    pending: [],
    posterIntake: [],
    creatorPartnerIntake: [],
    bookings: [],
    bookingEvents: [],
    users: [],
    organizers: [],
    creators: [],
    ads: [],
    categories: [],
    languages: [],
    cities: [],
    appOptions: {},
    settings: null,
    analytics: null,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(requestedTab);
  const loadGeneration = useRef(0);

  const loadAll = async () => {
    const generation = ++loadGeneration.current;
    setRefreshing(true);
    const updateData = (key, value) => {
      if (loadGeneration.current !== generation) return;
      setData((current) => ({ ...current, [key]: value }));
    };
    const detailRequests = [
      ["movies", () => api.get("/movies")],
      ["bookingEvents", async () => {
        const response = await api.get("/admin/events?status=all");
        updateData("events", response.data.filter((event) => event.status === "approved"));
        return response;
      }],
      ["pending", () => api.get("/events/pending")],
      ["posterIntake", () => api.get("/admin/poster-intake")],
      ["creatorPartnerIntake", () => api.get("/admin/creator-partner-intake")],
      ["bookings", () => api.get("/admin/bookings")],
      ["users", () => api.get("/admin/users")],
      ["organizers", () => api.get("/admin/organizers/finance")],
      ["creators", () => api.get("/admin/creators")],
      ["ads", () => api.get("/admin/ads")],
      ["categories", () => api.get("/admin/categories")],
      ["languages", () => api.get("/languages")],
      ["cities", () => api.get("/admin/cities")],
      ["appOptions", () => api.get("/app-options")],
      ["settings", () => api.get("/admin/settings")],
      ["analytics", () => api.get("/admin/analytics/summary")],
    ];
    const detailsPromise = Promise.allSettled(detailRequests.map(async ([key, request]) => {
      const response = await request();
      updateData(key, response.data);
    }));

    try {
      const stats = await api.get("/admin/stats");
      updateData("stats", stats.data);
    } catch {
      // The remaining sections can still load independently when stats is unavailable.
    } finally {
      if (loadGeneration.current === generation) setLoading(false);
    }

    await detailsPromise;
    if (loadGeneration.current === generation) setRefreshing(false);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (requestedTab && requestedTab !== activeTab) setActiveTab(requestedTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  const changeTab = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === "overview") {
      next.delete("tab");
    } else {
      next.set("tab", value);
    }
    if (!["pending", "poster-intake", "creator-loop"].includes(value)) next.delete("event");
    setSearchParams(next, { replace: true });
  };

  const metrics = useMemo(() => buildMetrics(data), [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="page-shell py-10">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading admin console
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="page-shell py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-black text-primary">Console</p>
            <h1 className="mt-1 font-display text-3xl font-black md:text-4xl">Admin Panel</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Manage listings, organisers, bookings, adverts, language coverage and operational settings from one place.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={loadAll} disabled={refreshing} className="rounded-full">
              {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Refresh
            </Button>
            <Button onClick={async () => {
              await api.post("/admin/eventbrite/sync");
              toast.success("Eventbrite sync started");
              loadAll();
            }} className="rounded-full" data-testid="eventbrite-sync-btn">
              <RadioTower className="mr-2 h-4 w-4" />
              Sync Eventbrite
            </Button>
          </div>
        </div>

        <section className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-7">
          <MetricCard label="Gross sales" value={`£${metrics.revenue.toFixed(2)}`} detail={`${metrics.confirmedBookings} active bookings`} icon={PoundSterling} tone="primary" />
          <MetricCard label="Website views" value={Number(data.analytics?.totals?.all_time || 0).toLocaleString()} detail={`${Number(data.analytics?.totals?.today || 0).toLocaleString()} today`} icon={Eye} />
          <MetricCard label="Users" value={data.stats?.users ?? data.users.length} detail={`${metrics.admins} admins, ${metrics.organisers} organisers`} icon={UsersIcon} />
          <MetricCard label="Listings" value={metrics.totalListings} detail={`${data.movies.length} movies, ${data.events.length} events`} icon={Film} />
          <MetricCard label="Pending" value={data.pending.length} detail="Events awaiting moderation" icon={Clock3} tone={data.pending.length ? "warn" : "ok"} />
          <MetricCard label="Bookings" value={data.stats?.bookings ?? data.bookings.length} detail={`${metrics.scannedBookings} checked in`} icon={Ticket} />
          <MetricCard label="Ads live" value={metrics.activeAds} detail={`${metrics.totalAdClicks} clicks`} icon={Megaphone} />
        </section>

        <Tabs value={activeTab} onValueChange={changeTab} className="mt-6">
          <TabsList className="sticky top-16 z-20 flex h-auto flex-wrap justify-start gap-1 rounded-lg border border-border bg-muted/60 p-1 backdrop-blur">
            <AdminTab value="overview" icon={BarChart3} label="Overview" />
            <AdminTab value="analytics" icon={Eye} label="Visits" />
            <AdminTab value="movies" icon={Film} label="Movies" />
            <AdminTab value="events" icon={Calendar} label="Events" />
            <AdminTab value="pending" icon={Layers} label="Pending" count={data.pending.length} />
            <AdminTab value="poster-intake" icon={Inbox} label="Poster Intake" count={data.posterIntake.filter((e) => ["pending", "resubmitted"].includes(e.status)).length} />
            <AdminTab value="creator-loop" icon={Instagram} label="Creator Loop" count={data.creatorPartnerIntake.filter((row) => ["new", "contacted", "permission_granted"].includes(row.status)).length} />
            <AdminTab value="bookings" icon={Ticket} label="Bookings" />
            <AdminTab value="users" icon={UsersIcon} label="Users" />
            <AdminTab value="organizers" icon={CreditCard} label="Organisers" count={data.organizers.length} />
            <AdminTab value="creators" icon={Sparkles} label="Creators" count={data.creators.filter((c) => c.public_profile && c.creator_opt_in).length} />
            <AdminTab value="ads" icon={Megaphone} label="Ads" />
            <AdminTab value="categories" icon={SlidersHorizontal} label="Categories" />
            <AdminTab value="campaigns" icon={Sparkles} label="Campaigns" />
            <AdminTab value="languages" icon={LangIcon} label="Languages" />
            <AdminTab value="cities" icon={MapPin} label="Cities" />
            <AdminTab value="options" icon={Settings} label="Options" />
            <AdminTab value="settings" icon={Settings} label="Settings" />
            <AdminTab value="knowledge" icon={BookOpen} label="Knowledge Base" />
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab data={data} metrics={metrics} reload={loadAll} />
          </TabsContent>
          <TabsContent value="analytics" className="mt-6">
            <WebsiteAnalyticsTab analytics={data.analytics} reload={loadAll} />
          </TabsContent>
          <TabsContent value="movies" className="mt-6">
            <MoviesTab rows={data.movies} setRows={(movies) => setData((d) => ({ ...d, movies }))} />
          </TabsContent>
          <TabsContent value="events" className="mt-6">
            <EventsTab
              rows={data.events}
              setRows={(events) => setData((d) => ({ ...d, events }))}
              reload={loadAll}
              categories={data.categories}
              languages={data.languages}
              cities={data.cities}
              campaigns={data.appOptions.event_campaigns || []}
              creators={data.creators}
            />
          </TabsContent>
          <TabsContent value="pending" className="mt-6">
            <PendingTab rows={data.pending} reload={loadAll} focusedEventId={focusedEventId} />
          </TabsContent>
          <TabsContent value="poster-intake" className="mt-6">
            <PosterIntakeTab rows={data.posterIntake} reload={loadAll} focusedEventId={focusedEventId} />
          </TabsContent>
          <TabsContent value="creator-loop" className="mt-6">
            <CreatorPartnerLoopTab rows={data.creatorPartnerIntake} reload={loadAll} />
          </TabsContent>
          <TabsContent value="bookings" className="mt-6">
            <BookingsTab rows={data.bookings} events={data.bookingEvents} />
          </TabsContent>
          <TabsContent value="users" className="mt-6">
            <UsersTab rows={data.users} />
          </TabsContent>
          <TabsContent value="organizers" className="mt-6">
            <OrganizersTab rows={data.organizers} reload={loadAll} />
          </TabsContent>
          <TabsContent value="creators" className="mt-6">
            <CreatorsAdminTab rows={data.creators} reload={loadAll} />
          </TabsContent>
          <TabsContent value="ads" className="mt-6">
            <AdsTab rows={data.ads} reload={loadAll} />
          </TabsContent>
          <TabsContent value="categories" className="mt-6">
            <CategoriesTab rows={data.categories} reload={loadAll} />
          </TabsContent>
          <TabsContent value="campaigns" className="mt-6">
            <CampaignsTab rows={data.appOptions.event_campaigns || []} reload={loadAll} />
          </TabsContent>
          <TabsContent value="languages" className="mt-6">
            <LanguagesTab rows={data.languages} reload={loadAll} />
          </TabsContent>
          <TabsContent value="cities" className="mt-6">
            <CitiesTab rows={data.cities} reload={loadAll} />
          </TabsContent>
          <TabsContent value="options" className="mt-6">
            <AppOptionsTab options={data.appOptions} reload={loadAll} />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsTab settings={data.settings} users={data.users} languages={data.languages} cities={data.cities} reload={loadAll} />
          </TabsContent>
          <TabsContent value="knowledge" className="mt-6">
            <section>
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <BookOpen className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-black">Admin knowledge base</h2>
                  <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                    Operating guidance for moderation, ticketing, finance, scanner access, campaigns, creators and account controls.
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <KnowledgeDirectory sections={adminKnowledgeSections} placeholder="Search admin procedures" />
              </div>
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function AdminTab({ value, icon: Icon, label, count }) {
  return (
    <TabsTrigger value={value} className="gap-2 rounded-md px-3 data-[state=active]:bg-background data-[state=active]:shadow">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      {count > 0 && <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] text-primary-foreground">{count}</span>}
    </TabsTrigger>
  );
}

function MetricCard({ label, value, detail, icon: Icon, tone = "neutral" }) {
  const toneClass = {
    primary: "bg-primary/10 text-primary",
    warn: "bg-amber-500/12 text-amber-700",
    ok: "bg-emerald-500/12 text-emerald-600",
    neutral: "bg-muted text-muted-foreground",
  }[tone];
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
          <p className="mt-2 font-display text-2xl font-black">{value}</p>
        </div>
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${toneClass}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

function OverviewTab({ data, metrics, reload }) {
  const latestBookings = data.bookings.slice(0, 5);
  const latestUsers = [...data.users]
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
    .slice(0, 5);
  const scanRate = metrics.confirmedBookings ? Math.round((metrics.scannedBookings / metrics.confirmedBookings) * 100) : 0;
  const adCtr = metrics.totalAdImpressions ? ((metrics.totalAdClicks / metrics.totalAdImpressions) * 100).toFixed(2) : "0.00";

  return (
    <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <section className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Operations</p>
            <h2 className="mt-1 font-display text-2xl font-black">Today's control room</h2>
          </div>
          <Badge variant="outline" className={data.pending.length ? statusStyles.pending : statusStyles.approved}>
            {data.pending.length ? `${data.pending.length} pending review` : "Moderation clear"}
          </Badge>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <Insight label="Event capacity" value={metrics.capacity} note={`${metrics.ticketsSold} tickets sold`} />
          <Insight label="Check-in rate" value={`${scanRate}%`} note="Scanned against active bookings" />
          <Insight label="Ad CTR" value={`${adCtr}%`} note={`${metrics.totalAdImpressions} impressions`} />
        </div>
        <div className="mt-5 space-y-4">
          <Progress value={Math.min(100, scanRate)} />
          <div className="grid gap-3 md:grid-cols-3">
            <QuickAction icon={Layers} label="Review pending events" value={data.pending.length} tone="warn" />
            <QuickAction icon={Bell} label="Broadcast enabled" value={data.users.length} />
            <QuickAction icon={RadioTower} label="Eventbrite imports" value={data.events.filter((e) => e.is_eventbrite).length} />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Recent Activity</p>
        <div className="mt-4 space-y-3">
          {latestBookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bookings yet.</p>
          ) : latestBookings.map((b) => (
            <div key={b.booking_id} className="flex items-start justify-between gap-3 border-b border-border pb-3 last:border-0 last:pb-0">
              <div>
                <p className="line-clamp-1 text-sm font-bold">{b.item_title}</p>
                <p className="text-xs text-muted-foreground">{b.user_name || b.user_email || "Guest"} · {formatDate(b.created_at)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-black">£{money(b.total)}</p>
                <StatusBadge status={b.status} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card p-5 xl:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Queue</p>
            <h2 className="mt-1 font-display text-xl font-black">Next admin actions</h2>
          </div>
          <Button variant="outline" onClick={reload} className="rounded-full">
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload queue
          </Button>
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <QueueBlock title="Pending Events" rows={data.pending.slice(0, 4)} empty="No events awaiting approval" getPrimary={(e) => e.title} getMeta={(e) => `${e.city || "Unknown city"} · ${e.date || "No date"}`} />
          <QueueBlock title="Latest Users" rows={latestUsers} empty="No users yet" getPrimary={(u) => u.name || u.email} getMeta={(u) => `${u.role || "user"} · ${formatDate(u.created_at)}`} />
          <QueueBlock title="Active Ads" rows={data.ads.filter((a) => a.active).slice(0, 4)} empty="No live ads" getPrimary={(a) => a.event?.title || a.event_id} getMeta={(a) => `${a.format} · ${a.clicks || 0} clicks`} />
        </div>
      </section>
    </div>
  );
}

function WebsiteAnalyticsTab({ analytics, reload }) {
  const [enabled, setEnabled] = useState(true);
  const [excludedIps, setExcludedIps] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(analytics?.settings?.enabled !== false);
    setExcludedIps((analytics?.settings?.excluded_ips || []).join("\n"));
  }, [analytics]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/analytics/settings", {
        enabled,
        excluded_ips: excludedIps.split(/[,\n]/).map((value) => value.trim()).filter(Boolean),
      });
      toast.success("Website analytics settings updated");
      await reload();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update website analytics");
    } finally {
      setSaving(false);
    }
  };

  const addCurrentIp = () => {
    const current = analytics?.current_ip;
    if (!current) return;
    const values = excludedIps.split(/[,\n]/).map((value) => value.trim()).filter(Boolean);
    if (!values.includes(current)) setExcludedIps([...values, current].join("\n"));
  };

  const totals = analytics?.totals || {};
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Website Analytics</p>
          <h2 className="mt-1 font-display text-2xl font-black">Anonymous website statistics</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Aggregate first-party statistics with a free user opt-out. Raw IPs, full browser signatures and visitor identifiers are not stored.
          </p>
        </div>
        <Badge variant="outline" className={analytics?.current_ip_excluded ? statusStyles.approved : statusStyles.pending}>
          Current IP {analytics?.current_ip_excluded ? "excluded" : "included"}
        </Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Today" value={Number(totals.today || 0).toLocaleString()} detail="Page views since midnight UTC" icon={Eye} tone="primary" />
        <MetricCard label="Last 7 days" value={Number(totals.last_7_days || 0).toLocaleString()} detail="Rolling seven-day total" icon={BarChart3} />
        <MetricCard label="Last 30 days" value={Number(totals.last_30_days || 0).toLocaleString()} detail="Rolling thirty-day total" icon={Calendar} />
        <MetricCard label="All time" value={Number(totals.all_time || 0).toLocaleString()} detail={`${analytics?.excluded_ip_count || 0} internal IP rules active`} icon={Sparkles} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="font-display text-xl font-black">Top pages</h3>
          <p className="mt-1 text-sm text-muted-foreground">Most viewed routes during the last 30 days.</p>
          <div className="mt-4 space-y-3">
            {(analytics?.top_pages || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No anonymous page views recorded yet.</p>
            ) : analytics.top_pages.map((row) => (
              <div key={row.path} className="flex items-center justify-between gap-4 border-b border-border pb-3 last:border-0 last:pb-0">
                <span className="min-w-0 truncate text-sm font-semibold">{row.path}</span>
                <span className="shrink-0 font-black tabular-nums">{Number(row.views || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="font-display text-xl font-black">Internal traffic exclusions</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter one IPv4, IPv6 or CIDR range per line. Easy-login allowlisted IPs are excluded automatically.
          </p>
          <label className="mt-4 flex items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} className="h-4 w-4 accent-primary" />
            Enable website view counting
          </label>
          <Textarea
            value={excludedIps}
            onChange={(event) => setExcludedIps(event.target.value)}
            placeholder={"203.0.113.25\n2001:db8::/32"}
            className="mt-4 min-h-32 font-mono text-sm"
            aria-label="Excluded website analytics IP addresses"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={addCurrentIp} disabled={!analytics?.current_ip} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Add current IP
            </Button>
            <Button type="button" onClick={save} disabled={saving} className="rounded-full">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Save exclusions
            </Button>
          </div>
          <p className="mt-3 break-all text-xs text-muted-foreground">
            Current request IP: {analytics?.current_ip || "Unavailable"}
          </p>
          {(analytics?.inherited_internal_ips || []).length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {analytics.inherited_internal_ips.length} easy-login internal IP rule{analytics.inherited_internal_ips.length === 1 ? " is" : "s are"} also excluded.
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <AnalyticsBreakdown title="Operating systems" rows={analytics?.platforms} />
        <AnalyticsBreakdown title="Device types" rows={analytics?.devices} />
        <AnalyticsBreakdown title="Browsers" rows={analytics?.browsers} />
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-display text-xl font-black">Daily views</h3>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {(analytics?.daily || []).slice(-14).map((row) => (
            <div key={row.day} className="border-l-2 border-primary pl-3 py-1">
              <p className="text-xs text-muted-foreground">{row.day}</p>
              <p className="font-display text-xl font-black tabular-nums">{Number(row.views || 0).toLocaleString()}</p>
            </div>
          ))}
          {(analytics?.daily || []).length === 0 && <p className="text-sm text-muted-foreground">Daily totals will appear after the first counted visit.</p>}
        </div>
      </div>
    </section>
  );
}

function AnalyticsBreakdown({ title, rows = [] }) {
  const total = rows.reduce((sum, row) => sum + Number(row.views || 0), 0);
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="font-display text-lg font-black">{title}</h3>
      <div className="mt-4 space-y-3">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No data yet.</p>
        ) : rows.map((row) => (
          <div key={row.name}>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-semibold">{row.name}</span>
              <span className="font-black tabular-nums">{Number(row.views || 0).toLocaleString()}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary" style={{ width: `${total ? Math.max(3, (Number(row.views || 0) / total) * 100) : 0}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MoviesTab({ rows, setRows }) {
  const [query, setQuery] = useState("");
  const [language, setLanguage] = useState("All");
  const languages = unique(rows.map((m) => m.language));
  const filtered = rows.filter((m) => matchesQuery(m, query, ["title", "language", "genre"]) && (language === "All" || m.language === language));
  const fetch = () => api.get("/movies").then((r) => setRows(r.data));

  return (
    <section className="space-y-4">
      <Toolbar title="Movie Library" count={filtered.length} query={query} setQuery={setQuery} filters={(
        <FilterPill options={["All", ...languages]} value={language} onChange={setLanguage} />
      )} />
      <DataTable>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Language</TableHead>
              <TableHead>Genre</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((m) => (
              <TableRow key={m.movie_id}>
                <TableCell>
                  <p className="font-bold">{m.title}</p>
                  <p className="text-xs text-muted-foreground">{m.duration || "Runtime not set"}</p>
                </TableCell>
                <TableCell>{m.language}</TableCell>
                <TableCell>{m.genre}</TableCell>
                <TableCell><Badge variant="outline">{m.rating}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/movies/${m.movie_id}`); await fetch(); toast.success("Movie deleted"); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataTable>
    </section>
  );
}

function EventsTab({ rows, setRows, reload, categories = [], languages = [], cities = [], campaigns = [], creators = [] }) {
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("All");
  const [editing, setEditing] = useState(null);
  const [updatingViewCounter, setUpdatingViewCounter] = useState("");
  const filterCities = unique(rows.map((e) => e.city));
  const filtered = rows.filter((e) => matchesQuery(e, query, ["title", "language", "city", "venue"]) && (city === "All" || e.city === city));
  const fetch = () => api.get("/admin/events?status=approved").then((r) => setRows(r.data));
  const setViewCounterEnabled = async (event, enabled) => {
    setUpdatingViewCounter(event.event_id);
    try {
      const { data } = await api.put(`/admin/events/${event.event_id}/view-counter`, { enabled });
      setRows((current) => current.map((row) => row.event_id === event.event_id ? { ...row, ...data } : row));
      toast.success(`View counter ${enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update view counter");
    } finally {
      setUpdatingViewCounter("");
    }
  };

  return (
    <section className="space-y-4">
      <Toolbar title="Approved Events" count={filtered.length} query={query} setQuery={setQuery} filters={(
        <FilterPill options={["All", ...filterCities]} value={city} onChange={setCity} icon={MapPin} />
      )} action={(
        <Button onClick={async () => { await api.post("/admin/eventbrite/sync"); toast.success("Eventbrite sync started"); reload(); }} className="rounded-full">
          <RadioTower className="mr-2 h-4 w-4" />
          Sync Eventbrite
        </Button>
      )} />
      <DataTable>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Tickets</TableHead>
              <TableHead>Views</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((e) => (
              <TableRow key={e.event_id}>
                <TableCell>
                  <p className="font-bold">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{e.language}{e.festival_campaign ? ` · ${e.festival_campaign}` : ""} · {e.venue || "Venue TBC"}</p>
                </TableCell>
                <TableCell>{e.city}</TableCell>
                <TableCell>{e.date}</TableCell>
                <TableCell>{e.tickets_sold || 0}/{e.capacity || 0}</TableCell>
                <TableCell>
                  <div className="flex min-w-[118px] items-center gap-3">
                    <span className="inline-flex items-center gap-1 font-bold tabular-nums">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                      {Number(e.view_count || 0).toLocaleString()}
                    </span>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-muted-foreground" title="Enable or disable event view tracking">
                      <input
                        type="checkbox"
                        checked={e.view_counter_enabled !== false}
                        disabled={updatingViewCounter === e.event_id}
                        onChange={(event) => setViewCounterEnabled(e, event.target.checked)}
                        className="h-4 w-4 accent-primary"
                        aria-label={`Track views for ${e.title}`}
                      />
                      Track
                    </label>
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline">{e.is_eventbrite ? "Eventbrite" : "Internal"}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(e)} title="Edit approved event">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/events/${e.event_id}`); await fetch(); toast.success("Event deleted"); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataTable>
      {editing && (
        <AdminEventEditor
          event={editing}
          categories={categories}
          languages={languages}
          cities={cities}
          campaigns={campaigns}
          creators={creators}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await fetch();
            await reload();
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

const eventFormDefaults = {
  title: "",
  description: "",
  category: "Music",
  festival_campaign: "",
  language: "Hindi",
  date: "",
  time: "",
  end_date: "",
  end_time: "",
  schedule_notes: "",
  venue: "",
  venue_address: "",
  city: "London",
  poster: "",
  ticketing_mode: "namaste",
  ticketing_approved: true,
  external_ticket_url: "",
  inquiry_phone: "",
  inquiry_whatsapp: "",
  inquiry_email: "",
  ticket_template: "tiered",
  ticket_notes: "",
  min_per_order: 1,
  max_per_order: 8,
  layout_file: "",
  layout_file_name: "",
  layout_file_type: "",
  layout_notes: "",
  is_eventbrite: false,
  eventbrite_url: "",
  recommended_creator_ids: [],
};

const toEventEditForm = (event) => ({
  ...eventFormDefaults,
  ...Object.fromEntries(Object.keys(eventFormDefaults).map((key) => [key, event?.[key] ?? eventFormDefaults[key]])),
  end_date: event?.end_date || "",
  end_time: event?.end_time || "",
  external_ticket_url: event?.external_ticket_url || "",
  eventbrite_url: event?.eventbrite_url || "",
  recommended_creator_ids: Array.isArray(event?.recommended_creator_ids) ? event.recommended_creator_ids : [],
  ticketing_approved: event?.ticketing_approved ?? (event?.ticketing_mode || "namaste") === "namaste",
  min_per_order: event?.min_per_order || 1,
  max_per_order: event?.max_per_order || 8,
});

const toEventPrices = (event) => {
  const prices = Array.isArray(event?.prices) ? event.prices : [];
  if (prices.length) {
    return prices.map((price, index) => ({
      ticket_key: price.ticket_key || `admin-tier-${index + 1}`,
      tier: price.tier || "General",
      price: Number(price.price ?? 0),
      capacity: Number(price.capacity ?? 0),
      description: price.description || "",
      scope: price.scope || (Array.isArray(price.valid_dates) && price.valid_dates.length > 1 ? "multi_day" : "single_day"),
      valid_dates: Array.isArray(price.valid_dates) ? price.valid_dates : [],
      scan_policy: price.scan_policy || "once",
    }));
  }
  return [{ ticket_key: "admin-general", tier: "General", price: 0, capacity: 0, description: "", scope: "single_day", valid_dates: [], scan_policy: "once" }];
};

function AdminEventEditor({ event, categories = [], languages = [], cities = [], campaigns = [], creators = [], onClose, onSaved }) {
  const [form, setForm] = useState(() => toEventEditForm(event));
  const [prices, setPrices] = useState(() => toEventPrices(event));
  const [saving, setSaving] = useState(false);
  const categoryOptions = unique([form.category, ...asArray(categories).map((c) => c.name).filter(Boolean)]);
  const languageOptions = unique([form.language, ...asArray(languages).map((l) => l.name).filter(Boolean)]);
  const cityOptions = unique([form.city, ...asArray(cities).map((c) => typeof c === "string" ? c : c.name).filter(Boolean)]);
  const campaignRows = asArray(campaigns).filter((item) => item && item.active !== false && item.key && item.label);
  const campaignOptions = form.festival_campaign && !campaignRows.some((item) => item.key === form.festival_campaign)
    ? [{ key: form.festival_campaign, label: form.festival_campaign }, ...campaignRows]
    : campaignRows;
  const campaignLabels = Object.fromEntries(campaignOptions.map((item) => [item.key, item.label]));
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const selectableCreators = asArray(creators).filter((creator) => creator.user_id && (creator.creator_opt_in || creator.public_profile || creator.featured_creator || creator.instagram_handle || creator.instagram_url));
  const updatePrice = (index, key, value) => setPrices((current) => current.map((row, i) => i === index ? { ...row, [key]: value } : row));
  const addPrice = () => setPrices((current) => [...current, {
    ticket_key: `admin-tier-${Date.now()}`,
    tier: "General",
    price: 0,
    capacity: 0,
    description: "",
    scope: "single_day",
    valid_dates: [],
    scan_policy: "once",
  }]);
  const removePrice = (index) => setPrices((current) => current.length === 1 ? current : current.filter((_, i) => i !== index));
  const save = async () => {
    if (!form.title || !form.date || !form.time || !form.venue) {
      toast.error("Title, date, time and venue are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        end_date: form.end_date || null,
        end_time: form.end_time || null,
        external_ticket_url: form.external_ticket_url || null,
        eventbrite_url: form.eventbrite_url || null,
        min_per_order: Number(form.min_per_order) || 1,
        max_per_order: Number(form.max_per_order) || 8,
        ticketing_approved: form.ticketing_mode === "namaste" ? Boolean(form.ticketing_approved) : false,
        recommended_creator_ids: asArray(form.recommended_creator_ids).filter(Boolean),
        prices: prices.map((price) => ({
          ...price,
          price: Number(price.price) || 0,
          capacity: Number(price.capacity) || 0,
          valid_dates: Array.isArray(price.valid_dates)
            ? price.valid_dates.filter(Boolean)
            : String(price.valid_dates || "").split(",").map((item) => item.trim()).filter(Boolean),
          scan_policy: (price.scope || "single_day") === "multi_day" ? "once_per_day" : "once",
        })),
      };
      await api.put(`/events/${event.event_id}`, payload);
      toast.success("Approved event updated");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-background/80 px-4 py-6 backdrop-blur">
      <div className="w-full max-w-5xl rounded-lg border border-border bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-border bg-card p-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Edit approved event</p>
            <h2 className="mt-1 font-display text-2xl font-black">{event.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Changes save directly to the live approved listing.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid gap-5 p-5 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-5">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Title"><Input value={form.title} onChange={(e) => setField("title", e.target.value)} /></Field>
              <Field label="Category">
                <NativeSelect value={form.category} onChange={(value) => setField("category", value)} options={categoryOptions} />
              </Field>
              <Field label="Festival / campaign">
                <NativeSelect
                  value={form.festival_campaign || "none"}
                  onChange={(value) => setField("festival_campaign", value === "none" ? "" : value)}
                  options={["none", ...campaignOptions.map((item) => item.key)]}
                  labels={{ none: "None", ...campaignLabels }}
                />
              </Field>
              <Field label="Language">
                <NativeSelect value={form.language} onChange={(value) => setField("language", value)} options={languageOptions} />
              </Field>
              <Field label="City">
                <NativeSelect value={form.city} onChange={(value) => setField("city", value)} options={cityOptions} />
              </Field>
              <Field label="Start date"><Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} /></Field>
              <Field label="Start time"><Input type="time" value={form.time} onChange={(e) => setField("time", e.target.value)} /></Field>
              <Field label="End date"><Input type="date" value={form.end_date || ""} onChange={(e) => setField("end_date", e.target.value)} /></Field>
              <Field label="End time"><Input type="time" value={form.end_time || ""} onChange={(e) => setField("end_time", e.target.value)} /></Field>
              <Field label="Venue"><Input value={form.venue} onChange={(e) => setField("venue", e.target.value)} /></Field>
              <Field label="Venue address"><Input value={form.venue_address} onChange={(e) => setField("venue_address", e.target.value)} /></Field>
            </div>
            <Field label="Description">
              <Textarea rows={5} value={form.description} onChange={(e) => setField("description", e.target.value)} />
            </Field>
            <Field label="Schedule notes">
              <Textarea rows={3} value={form.schedule_notes} onChange={(e) => setField("schedule_notes", e.target.value)} />
            </Field>
            <Field label="Poster URL / data URL">
              <Input value={form.poster} onChange={(e) => setField("poster", e.target.value)} />
            </Field>
            <CreatorEventPicker
              creators={selectableCreators}
              selected={form.recommended_creator_ids}
              onChange={(ids) => setField("recommended_creator_ids", ids)}
            />
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-bold">Ticket rows</p>
                  <p className="text-xs text-muted-foreground">Edit user-visible ticket names, prices, capacities and valid dates.</p>
                </div>
                <Button size="sm" variant="outline" onClick={addPrice} className="rounded-full">
                  <Plus className="mr-1 h-4 w-4" />
                  Add row
                </Button>
              </div>
              <div className="mt-4 space-y-3">
                {prices.map((price, index) => (
                  <div key={price.ticket_key || index} className="rounded-lg border border-border bg-background/60 p-3">
                    <div className="grid gap-2 md:grid-cols-[1.2fr_0.7fr_0.7fr]">
                      <Input placeholder="Ticket name" value={price.tier} onChange={(e) => updatePrice(index, "tier", e.target.value)} />
                      <Input type="number" min="0" step="0.01" placeholder="Price" value={price.price} onChange={(e) => updatePrice(index, "price", e.target.value)} />
                      <Input type="number" min="0" placeholder="Capacity" value={price.capacity} onChange={(e) => updatePrice(index, "capacity", e.target.value)} />
                    </div>
                    <div className="mt-2 grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                      <Input placeholder="Description" value={price.description || ""} onChange={(e) => updatePrice(index, "description", e.target.value)} />
                      <Input placeholder="Valid dates comma separated, e.g. 2026-09-13" value={asArray(price.valid_dates).join(", ")} onChange={(e) => updatePrice(index, "valid_dates", e.target.value.split(",").map((item) => item.trim()).filter(Boolean))} />
                      <Button size="sm" variant="ghost" onClick={() => removePrice(index)} disabled={prices.length === 1}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-5">
            <div className="rounded-lg border border-border p-4">
              <p className="font-bold">Ticketing mode</p>
              <div className="mt-3 space-y-3">
                <Field label="Mode">
                  <NativeSelect
                    value={form.ticketing_mode}
                    onChange={(value) => setForm((current) => ({
                      ...current,
                      ticketing_mode: value,
                      ticketing_approved: value === "namaste" ? current.ticketing_approved : false,
                    }))}
                    options={["namaste", "external", "inquiry"]}
                    labels={ticketingLabels}
                  />
                </Field>
                {form.ticketing_mode === "namaste" && (
                  <label className="flex items-center gap-2 rounded-md border border-border p-3 text-sm font-bold">
                    <input type="checkbox" checked={Boolean(form.ticketing_approved)} onChange={(e) => setField("ticketing_approved", e.target.checked)} />
                    Namaste checkout approved
                  </label>
                )}
                <Field label="External ticket URL">
                  <Input value={form.external_ticket_url || ""} onChange={(e) => setField("external_ticket_url", e.target.value)} />
                </Field>
                <Field label="Inquiry phone">
                  <Input value={form.inquiry_phone || ""} onChange={(e) => setField("inquiry_phone", e.target.value)} />
                </Field>
                <Field label="Inquiry WhatsApp">
                  <Input value={form.inquiry_whatsapp || ""} onChange={(e) => setField("inquiry_whatsapp", e.target.value)} />
                </Field>
                <Field label="Inquiry email">
                  <Input value={form.inquiry_email || ""} onChange={(e) => setField("inquiry_email", e.target.value)} />
                </Field>
              </div>
            </div>
            <div className="rounded-lg border border-border p-4">
              <p className="font-bold">Advanced</p>
              <div className="mt-3 grid gap-3">
                <Field label="Ticket template"><Input value={form.ticket_template} onChange={(e) => setField("ticket_template", e.target.value)} /></Field>
                <Field label="Ticket notes"><Textarea rows={3} value={form.ticket_notes} onChange={(e) => setField("ticket_notes", e.target.value)} /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Min per order"><Input type="number" min="1" value={form.min_per_order} onChange={(e) => setField("min_per_order", e.target.value)} /></Field>
                  <Field label="Max per order"><Input type="number" min="1" value={form.max_per_order} onChange={(e) => setField("max_per_order", e.target.value)} /></Field>
                </div>
                <Field label="Layout file"><Input value={form.layout_file} onChange={(e) => setField("layout_file", e.target.value)} /></Field>
                <Field label="Layout notes"><Textarea rows={2} value={form.layout_notes} onChange={(e) => setField("layout_notes", e.target.value)} /></Field>
              </div>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t border-border bg-card p-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Save approved event
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreatorEventPicker({ creators = [], selected = [], onChange }) {
  const [query, setQuery] = useState("");
  const selectedIds = new Set(asArray(selected));
  const filtered = asArray(creators)
    .filter((creator) => {
      const haystack = [
        creator.display_name,
        creator.name,
        creator.email,
        creator.instagram_handle,
        ...(creator.creator_categories || []),
        ...(creator.creator_locations || []),
      ].join(" ").toLowerCase();
      return !query.trim() || haystack.includes(query.trim().toLowerCase());
    })
    .slice(0, 80);

  const toggle = (creatorId) => {
    if (!creatorId) return;
    const next = selectedIds.has(creatorId)
      ? asArray(selected).filter((id) => id !== creatorId)
      : [...asArray(selected), creatorId];
    onChange(next);
  };

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-bold">Community voices for this event</p>
          <p className="text-xs text-muted-foreground">Select one or more creators/influencers to show as recommending this event.</p>
        </div>
        <Badge variant="outline">{selectedIds.size} selected</Badge>
      </div>
      <Input
        className="mt-3"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search creator name, Instagram, category or location"
      />
      {filtered.length === 0 ? (
        <div className="mt-3 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
          No creator profiles found. Add or enable creators in the Creators tab first.
        </div>
      ) : (
        <div className="mt-3 grid max-h-80 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
          {filtered.map((creator) => {
            const name = creator.display_name || creator.name || creator.email || "Unnamed creator";
            const checked = selectedIds.has(creator.user_id);
            const categories = creator.creator_categories || [];
            const locations = creator.creator_locations || creator.city_preferences || [];
            return (
              <button
                key={creator.user_id}
                type="button"
                onClick={() => toggle(creator.user_id)}
                className={`rounded-lg border p-3 text-left transition ${checked ? "border-primary bg-primary/10" : "border-border bg-background/60 hover:border-primary/50"}`}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border border-border bg-background">
                    {checked && <CheckCircle2 className="h-4 w-4 text-primary" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-bold">{name}</span>
                    {creator.instagram_handle && <span className="block truncate text-xs font-bold text-primary">{creator.instagram_handle}</span>}
                    <span className="mt-1 block truncate text-xs text-muted-foreground">
                      {locations.slice(0, 2).join(", ") || "UK"}{categories.length ? ` · ${categories.slice(0, 2).join(", ")}` : ""}
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {selectedIds.size > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {asArray(selected).map((creatorId) => {
            const creator = creators.find((item) => item.user_id === creatorId);
            const label = creator?.display_name || creator?.name || creator?.instagram_handle || creatorId;
            return (
              <Badge key={creatorId} variant="secondary" className="gap-1">
                {label}
                <button type="button" onClick={() => toggle(creatorId)} className="ml-1 rounded-full hover:text-destructive">×</button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function NativeSelect({ value, onChange, options, labels = {} }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-bold text-foreground outline-none focus:ring-2 focus:ring-ring"
    >
      {options.map((option) => (
        <option key={option} value={option}>{labels[option] || option}</option>
      ))}
    </select>
  );
}

function PendingTab({ rows, reload, focusedEventId = "" }) {
  const [notes, setNotes] = useState({});
  const [ticketing, setTicketing] = useState({});
  const reviewNote = (eventId) => notes[eventId] || "";
  const setReviewNote = (eventId, note) => setNotes((current) => ({ ...current, [eventId]: note }));
  const reviewTicketing = (event) => ticketing[event.event_id] || {
    ticketing_mode: event.ticketing_mode || "namaste",
    ticketing_approved: event.ticketing_approved ?? (event.ticketing_mode || "namaste") === "namaste",
    external_ticket_url: event.external_ticket_url || "",
    inquiry_phone: event.inquiry_phone || "",
    inquiry_whatsapp: event.inquiry_whatsapp || "",
    inquiry_email: event.inquiry_email || "",
  };
  const setReviewTicketing = (event, patch) => setTicketing((current) => ({
    ...current,
    [event.event_id]: { ...reviewTicketing(event), ...patch },
  }));
  const approvePendingEvent = async (event) => {
    if (event.ticketing_change_request?.status === "pending" && event.status === "approved") {
      await approveTicketingChange(event);
      return;
    }
    const ticketingPayload = reviewTicketing(event);
    await api.post(`/events/${event.event_id}/approve`, {
      note: reviewNote(event.event_id),
      ...ticketingPayload,
      ticketing_approved: ticketingPayload.ticketing_mode === "namaste" ? Boolean(ticketingPayload.ticketing_approved) : false,
    });
    toast.success("Event approved");
    reload();
  };
  const approveTicketingChange = async (event) => {
    await api.post(`/events/${event.event_id}/ticketing-change-request/approve`, {
      note: reviewNote(event.event_id),
    });
    toast.success("Ticketing change approved");
    reload();
  };
  const rejectTicketingChange = async (event) => {
    await api.post(`/events/${event.event_id}/ticketing-change-request/reject`, {
      note: reviewNote(event.event_id),
    });
    toast.success("Ticketing change rejected");
    reload();
  };
  const requestChanges = async (event) => {
    const note = reviewNote(event.event_id).trim();
    if (!note) {
      toast.error("Add amendment notes before requesting changes");
      return;
    }
    await api.post(`/events/${event.event_id}/changes`, { note });
    toast.success("Amendments requested");
    reload();
  };

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
        <p className="mt-3 font-bold">No pending events</p>
        <p className="text-sm text-muted-foreground">
          {focusedEventId ? "That notification has already been handled or the event is no longer pending." : "New organiser submissions will appear here for approval."}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {rows.map((e) => (
        (() => {
          const t = reviewTicketing(e);
          const changeRequest = e.ticketing_change_request?.status === "pending" ? e.ticketing_change_request : null;
          return (
        <article
          key={e.event_id}
          className={`rounded-lg border bg-card p-4 ${focusedEventId === e.event_id ? "border-primary shadow-sm shadow-primary/20" : "border-border"}`}
        >
          <div className="flex gap-4">
            <img src={e.poster || "https://images.unsplash.com/photo-1518929458119-e5bf444c30f4?w=400"} alt="" className="h-24 w-32 rounded-md object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="line-clamp-1 font-bold">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{e.city} · {formatEventSchedule(e)} · {e.language}</p>
                </div>
                <StatusBadge status={e.status || "pending"} />
              </div>
              <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{e.description}</p>
              <div className="mt-3 grid gap-2 text-xs sm:grid-cols-5">
                <ReviewFact label="Ticketing" value={String(t.ticketing_mode || "namaste").replace("-", " ")} />
                <ReviewFact label="Template" value={String(e.ticket_template || "tiered").replace("-", " ")} />
                <ReviewFact label="Tiers" value={e.prices?.length || 0} />
                <ReviewFact label="Capacity" value={(e.prices || []).reduce((sum, p) => sum + (Number(p.capacity) || 0), 0)} />
                <ReviewFact label="Layout" value={e.layout_file ? "Attached" : "Missing"} />
              </div>
              {e.layout_notes && <p className="mt-2 text-xs text-muted-foreground">Layout: {e.layout_notes}</p>}
              <OrganizerContactCard event={e} />
              {changeRequest && (
                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-amber-700">Ticketing mode change request</p>
                  <div className="mt-2 grid gap-2 text-xs sm:grid-cols-4">
                    <ReviewFact label="Current" value={ticketingLabels[changeRequest.current_mode] || changeRequest.current_mode} />
                    <ReviewFact label="Requested" value={ticketingLabels[changeRequest.target_mode] || changeRequest.target_mode} />
                    <ReviewFact label="Active bookings" value={changeRequest.active_bookings || 0} />
                    <ReviewFact label="Requested by" value={changeRequest.requested_by_role || "organiser"} />
                  </div>
                  {changeRequest.note && <p className="mt-2 text-xs text-amber-800">Organiser note: {changeRequest.note}</p>}
                  {asArray(changeRequest.risks).length > 0 && (
                    <div className="mt-2 rounded-md border border-amber-500/30 bg-background/70 p-2 text-xs text-amber-800">
                      {changeRequest.risks.map((risk) => <p key={risk}>{risk}</p>)}
                    </div>
                  )}
                </div>
              )}
              {!changeRequest && <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Ticketing decision</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <select
                    value={t.ticketing_mode}
                    onChange={(event) => setReviewTicketing(e, {
                      ticketing_mode: event.target.value,
                      ticketing_approved: event.target.value === "namaste",
                    })}
                    className="h-9 rounded-md border border-border bg-background px-2 text-xs"
                  >
                    <option value="namaste">Namaste ticketing</option>
                    <option value="external">External ticket link</option>
                    <option value="inquiry">Inquiry only</option>
                  </select>
                  {t.ticketing_mode === "namaste" && (
                    <>
                      <label className="flex items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-bold">
                        <input type="checkbox" checked={Boolean(t.ticketing_approved)} onChange={(event) => setReviewTicketing(e, { ticketing_approved: event.target.checked })} />
                        Enable checkout
                      </label>
                      <Input className="sm:col-span-2 h-9 text-xs" value={t.external_ticket_url} onChange={(event) => setReviewTicketing(e, { external_ticket_url: event.target.value })} placeholder="Optional fallback organiser/payment URL until Stripe is ready" />
                    </>
                  )}
                  {t.ticketing_mode === "external" && (
                    <Input className="sm:col-span-2 h-9 text-xs" value={t.external_ticket_url} onChange={(event) => setReviewTicketing(e, { external_ticket_url: event.target.value })} placeholder="External ticket URL" />
                  )}
                  {t.ticketing_mode === "inquiry" && (
                    <>
                      <Input className="h-9 text-xs" value={t.inquiry_phone} onChange={(event) => setReviewTicketing(e, { inquiry_phone: event.target.value })} placeholder="Inquiry phone" />
                      <Input className="h-9 text-xs" value={t.inquiry_whatsapp} onChange={(event) => setReviewTicketing(e, { inquiry_whatsapp: event.target.value })} placeholder="WhatsApp number" />
                      <Input className="h-9 text-xs" value={t.inquiry_email} onChange={(event) => setReviewTicketing(e, { inquiry_email: event.target.value })} placeholder="Inquiry email" />
                    </>
                  )}
                </div>
              </div>}
              <div className="mt-3">
                <Textarea
                  rows={2}
                  value={reviewNote(e.event_id)}
                  onChange={(event) => setReviewNote(e.event_id, event.target.value)}
                  placeholder="Approval note or rejection reason visible to organiser"
                  className="text-xs"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" data-testid={`approve-${e.event_id}`} onClick={() => approvePendingEvent(e)} className="rounded-full">
                  {changeRequest ? "Approve ticketing change" : "Approve"}
                </Button>
                {!changeRequest && <Button size="sm" variant="secondary" onClick={() => requestChanges(e)} className="rounded-full">
                  Request amendments
                </Button>}
                <Button size="sm" variant="outline" onClick={async () => {
                  if (changeRequest) await rejectTicketingChange(e);
                  else { await api.post(`/events/${e.event_id}/reject`, { note: reviewNote(e.event_id) }); toast.success("Event rejected"); reload(); }
                }} className="rounded-full">
                  {changeRequest ? "Reject ticketing change" : "Reject"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => window.open(`/event/${e.event_id}`, "_blank")} className="rounded-full">
                  Preview
                </Button>
              </div>
            </div>
          </div>
        </article>
          );
        })()
      ))}
    </div>
  );
}

function PosterIntakeTab({ rows, reload, focusedEventId = "" }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [posterPreview, setPosterPreview] = useState(null);
  const statuses = unique(rows.map((row) => row.status || "pending"));
  const filtered = rows.filter((row) => {
    const extraction = row.poster_extraction || {};
    const haystack = [
      row.title,
      row.city,
      row.venue,
      row.source_group,
      row.source_sender,
      row.source_phone,
      row.organizer_name,
      extraction.raw_caption,
      extraction.raw_ocr_text,
      row.event_id,
    ].join(" ").toLowerCase();
    return (!query.trim() || haystack.includes(query.trim().toLowerCase())) && (status === "All" || (row.status || "pending") === status);
  });
  const pendingCount = rows.filter((row) => ["pending", "resubmitted"].includes(row.status || "pending")).length;
  const missingCount = rows.filter((row) => asArray(row.poster_extraction?.missing_fields).length > 0).length;

  return (
    <section className="space-y-4">
      <Toolbar title="Poster Intake" count={filtered.length} query={query} setQuery={setQuery} filters={(
        <FilterPill options={["All", ...statuses]} value={status} onChange={setStatus} />
      )} />
      <div className="grid gap-3 md:grid-cols-3">
        <Insight label="Imported posters" value={rows.length} note="Drafts created from local/WhatsApp poster intake" />
        <Insight label="Needs admin review" value={pendingCount} note="Still pending or resubmitted" />
        <Insight label="Missing fields" value={missingCount} note="OCR could not infer all essentials" />
      </div>
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-bold">No poster imports found</p>
          <p className="text-sm text-muted-foreground">Drop a poster into the intake folder and run the local poster intake script.</p>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {filtered.map((event) => {
            const extraction = event.poster_extraction || {};
            const missing = asArray(extraction.missing_fields);
            const contactBits = [event.inquiry_whatsapp && `WhatsApp ${event.inquiry_whatsapp}`, event.inquiry_phone && `Phone ${event.inquiry_phone}`, event.inquiry_email && `Email ${event.inquiry_email}`].filter(Boolean);
            return (
              <article
                key={event.event_id}
                className={`rounded-lg border bg-card p-4 ${focusedEventId === event.event_id ? "border-primary shadow-sm shadow-primary/20" : "border-border"}`}
              >
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setPosterPreview(event)}
                    className="group relative h-28 w-28 shrink-0 overflow-hidden rounded-md border border-border bg-muted text-left"
                    title="View full poster"
                  >
                    <img src={event.poster || "https://images.unsplash.com/photo-1518929458119-e5bf444c30f4?w=400"} alt="" className="h-full w-full object-cover transition group-hover:scale-105" />
                    <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/60 px-2 py-1 text-[10px] font-bold text-white opacity-0 transition group-hover:opacity-100">
                      <Eye className="h-3 w-3" /> View
                    </span>
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="line-clamp-1 font-display text-lg font-black">{event.title || "Untitled poster draft"}</p>
                        <p className="text-xs text-muted-foreground">{formatEventSchedule(event)} · {event.city || "City TBC"} · {event.language || "Language TBC"}</p>
                      </div>
                      <StatusBadge status={event.status || "pending"} />
                    </div>
                    <div className="mt-3 grid gap-2 text-xs sm:grid-cols-4">
                      <ReviewFact label="Event ID" value={event.event_id} />
                      <ReviewFact label="Source" value={event.source_group || event.source || "local folder"} />
                      <ReviewFact label="Confidence" value={extraction.confidence || "unknown"} />
                      <ReviewFact label="Mode" value={ticketingLabels[event.ticketing_mode] || event.ticketing_mode || "Inquiry only"} />
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-border bg-muted/30 p-3">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Extracted details</p>
                    <p className="mt-2 text-sm font-bold">{event.venue || "Venue TBC"}</p>
                    <p className="text-xs text-muted-foreground">{contactBits.length ? contactBits.join(" · ") : "No contact extracted"}</p>
                    {missing.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {missing.map((field) => <Badge key={field} variant="outline" className={statusStyles.pending}>Missing {field}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-border bg-muted/30 p-3">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Source trail</p>
                    <p className="mt-2 text-xs text-muted-foreground">Sender: <span className="font-bold text-foreground">{event.source_sender || "Unknown"}</span></p>
                    <p className="text-xs text-muted-foreground">Phone: <span className="font-bold text-foreground">{event.source_phone || "Not provided"}</span></p>
                    <p className="text-xs text-muted-foreground">Created: <span className="font-bold text-foreground">{formatDate(event.created_at)}</span></p>
                  </div>
                </div>
                {(extraction.raw_caption || extraction.raw_ocr_text) && (
                  <details className="mt-3 rounded-lg border border-border bg-background p-3">
                    <summary className="cursor-pointer text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Caption and OCR text</summary>
                    {extraction.raw_caption && <p className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">Caption: {extraction.raw_caption}</p>}
                    {extraction.raw_ocr_text && <p className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">OCR: {extraction.raw_ocr_text}</p>}
                  </details>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {["pending", "resubmitted"].includes(event.status || "pending") && (
                    <Button size="sm" onClick={() => window.location.href = `/admin-panel?tab=pending&event=${event.event_id}`} className="rounded-full">
                      Review approval
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => window.open(`/create-event?edit=${event.event_id}`, "_blank")} className="rounded-full">
                    Amend draft
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPosterPreview(event)} className="rounded-full">
                    <Eye className="mr-1 h-4 w-4" /> View poster
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => window.open(`/event/${event.event_id}`, "_blank")} className="rounded-full">
                    Preview
                  </Button>
                  <Button size="sm" variant="ghost" onClick={async () => { await navigator.clipboard.writeText(event.event_id); toast.success("Event ID copied"); }} className="rounded-full">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {posterPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-lg border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div className="min-w-0">
                <p className="truncate font-bold">{posterPreview.title || "Poster preview"}</p>
                <p className="truncate text-xs text-muted-foreground">{posterPreview.event_id} · {posterPreview.city || "City TBC"} · {formatEventSchedule(posterPreview)}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setPosterPreview(null)} aria-label="Close poster preview">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="min-h-0 overflow-auto bg-black p-3">
              <img
                src={posterPreview.poster || "https://images.unsplash.com/photo-1518929458119-e5bf444c30f4?w=1200"}
                alt={`${posterPreview.title || "Event"} poster`}
                className="mx-auto max-h-[78vh] max-w-full rounded-md object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function CreatorPartnerLoopTab({ rows, reload }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [busyId, setBusyId] = useState("");
  const statuses = unique(rows.map((row) => row.status || "new"));
  const filtered = rows.filter((row) => {
    const haystack = [
      row.creator_handle,
      row.creator_name,
      row.caption,
      row.message,
      row.media_url,
      row.event_url,
      row.intake_id,
    ].join(" ").toLowerCase();
    return (!query.trim() || haystack.includes(query.trim().toLowerCase())) && (status === "All" || (row.status || "new") === status);
  });

  const update = async (row, patch, message) => {
    setBusyId(row.intake_id);
    try {
      await api.patch(`/admin/creator-partner-intake/${row.intake_id}`, patch);
      toast.success(message);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update creator intake");
    } finally {
      setBusyId("");
    }
  };

  const createEvent = async (row) => {
    setBusyId(row.intake_id);
    try {
      const { data } = await api.post(`/admin/creator-partner-intake/${row.intake_id}/create-event`);
      toast.success(data.idempotent ? "Existing event draft opened" : "Pending event draft created");
      window.location.href = `/admin-panel?tab=pending&event=${data.event.event_id}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create event draft");
      setBusyId("");
    }
  };

  const copyText = async (value, message) => {
    await navigator.clipboard.writeText(value || "");
    toast.success(message);
  };

  return (
    <section className="space-y-4">
      <Toolbar title="Creator Partner Loop" count={filtered.length} query={query} setQuery={setQuery} filters={(
        <FilterPill options={["All", ...statuses]} value={status} onChange={setStatus} />
      )} />
      <div className="grid gap-3 md:grid-cols-4">
        <Insight label="Submissions" value={rows.length} note="Tags, mentions and creator DMs" />
        <Insight label="Needs review" value={rows.filter((row) => row.status === "new").length} note="No outreach sent yet" />
        <Insight label="Permission granted" value={rows.filter((row) => row.permission_status === "granted").length} note="Approved for attribution or sharing" />
        <Insight label="Events created" value={rows.filter((row) => row.event_id).length} note="Pending or live Namaste events" />
      </div>
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <Instagram className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-bold">No creator submissions found</p>
          <p className="text-sm text-muted-foreground">Instagram tags, mentions and DMs received through the signed integration will appear here.</p>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {filtered.map((row) => {
            const isBusy = busyId === row.intake_id;
            const sourceUrl = row.media_url || row.creator_profile_url;
            return (
              <article key={row.intake_id} className="rounded-lg border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-lg font-black">{row.creator_name || row.creator_handle || "Unknown creator"}</p>
                      <StatusBadge status={row.status || "new"} />
                      <Badge variant="outline">{row.interaction || "mention"}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{row.creator_handle || "No handle"} · {row.platform || "instagram"} · received {formatDate(row.received_at || row.created_at)}</p>
                  </div>
                  <Select value={row.status || "new"} onValueChange={(value) => update(row, { status: value }, "Creator intake updated")} disabled={isBusy}>
                    <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[
                        ["new", "New"],
                        ["contacted", "Contacted"],
                        ["permission_granted", "Permission granted"],
                        ["event_created", "Event created"],
                        ["closed", "Closed"],
                        ["rejected", "Rejected"],
                      ].map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border pt-3 text-xs sm:grid-cols-4">
                  <CreatorLoopFact label="Creator" value={row.creator_match === "matched" ? "Matched" : "Unmatched"} />
                  <CreatorLoopFact label="Permission" value={row.permission_status || "unknown"} />
                  <CreatorLoopFact label="Share" value={row.share_status || "not requested"} />
                  <CreatorLoopFact label="Received" value={row.received_count || 1} />
                </div>

                {(row.caption || row.message) && (
                  <div className="mt-3 border-t border-border pt-3">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Creator content</p>
                    <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-sm">{row.caption || row.message}</p>
                  </div>
                )}

                <div className="mt-3 grid gap-4 border-t border-border pt-3 md:grid-cols-2 md:divide-x md:divide-border">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Outreach DM</p>
                    <p className="mt-2 line-clamp-5 whitespace-pre-wrap text-xs text-muted-foreground">{row.copy?.outreach || "No outreach copy prepared"}</p>
                    <Button size="sm" variant="ghost" className="mt-2 rounded-full" onClick={() => copyText(row.copy?.outreach, "Outreach message copied")} disabled={!row.copy?.outreach}>
                      <Copy className="mr-1 h-4 w-4" /> Copy DM
                    </Button>
                  </div>
                  <div className="md:pl-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Public reply</p>
                    <p className="mt-2 line-clamp-5 whitespace-pre-wrap text-xs text-muted-foreground">{row.copy?.reply || "No reply copy prepared"}</p>
                    <Button size="sm" variant="ghost" className="mt-2 rounded-full" onClick={() => copyText(row.copy?.reply, "Reply copied")} disabled={!row.copy?.reply}>
                      <Copy className="mr-1 h-4 w-4" /> Copy reply
                    </Button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {sourceUrl && <Button size="sm" variant="outline" asChild className="rounded-full"><a href={sourceUrl} target="_blank" rel="noreferrer"><Instagram className="mr-1 h-4 w-4" /> Open source</a></Button>}
                  {!row.event_id ? (
                    <Button size="sm" onClick={() => createEvent(row)} disabled={isBusy} className="rounded-full">
                      {isBusy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Create event draft
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => { window.location.href = `/admin-panel?tab=pending&event=${row.event_id}`; }} className="rounded-full">Review event</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => update(row, { status: "contacted", permission_status: "requested", share_status: "requested" }, "Outreach marked as sent")} disabled={isBusy} className="rounded-full">Mark contacted</Button>
                  <Button size="sm" variant="outline" onClick={() => update(row, { status: "permission_granted", permission_status: "granted", future_tag_opt_in: true }, "Permission recorded")} disabled={isBusy} className="rounded-full">Record permission</Button>
                  <Button size="sm" variant="ghost" onClick={() => update(row, { share_status: "shared" }, "Share recorded")} disabled={isBusy || row.share_status === "shared"} className="rounded-full">Shared post</Button>
                  <Button size="sm" variant="ghost" onClick={() => update(row, { follows_namaste: true }, "Follow recorded")} disabled={isBusy || row.follows_namaste === true} className="rounded-full">Follows us</Button>
                  <Button size="sm" variant="ghost" onClick={() => update(row, { future_tag_opt_in: true }, "Future tag opt-in recorded")} disabled={isBusy || row.future_tag_opt_in === true} className="rounded-full">Future tags</Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CreatorLoopFact({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="truncate font-bold capitalize">{String(value).replaceAll("_", " ")}</p>
    </div>
  );
}

function ReviewFact({ label, value }) {
  return (
    <div className="rounded-md bg-muted px-2 py-1.5">
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="font-bold">{value}</p>
    </div>
  );
}

function OrganizerContactCard({ event }) {
  const organizer = event.organizer || {};
  const name = event.organizer_name || organizer.display_name || organizer.name || "Unknown organiser";
  const email = event.organizer_email || organizer.email || "";
  const phone = event.organizer_phone || organizer.mobile || "";
  const whatsapp = String(event.organizer_whatsapp || event.inquiry_whatsapp || (organizer.whatsapp_opt_in ? organizer.mobile : "") || "").replace(/[^\d]/g, "");
  const instagram = organizer.instagram_url || (organizer.instagram_handle ? `https://instagram.com/${String(organizer.instagram_handle).replace(/^@/, "")}` : "");
  const stripeReady = Boolean(organizer.stripe_charges_enabled && organizer.stripe_payouts_enabled);
  const paymentMode = organizer.ticketing_payment_mode || "disabled";
  const routeReady = paymentMode === "platform" || (paymentMode === "stripe_connect" && stripeReady);

  return (
    <div className="mt-3 rounded-lg border border-border bg-background p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Organiser contact</p>
          <p className="mt-1 font-bold">{name}</p>
          <p className="text-[11px] text-muted-foreground">{event.organizer_id || "No organiser ID"}</p>
        </div>
        <Badge variant="outline" className={routeReady ? statusStyles.approved : paymentMode === "disabled" ? statusStyles.inactive : statusStyles.pending}>
          {paymentModeLabels[paymentMode] || paymentModeLabels.disabled}
        </Badge>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <ContactLink icon={Mail} label="Email" value={email} href={email ? `mailto:${email}?subject=${encodeURIComponent(`NamasteNetwork event review: ${event.title}`)}` : ""} />
        <ContactLink icon={Phone} label="Phone" value={phone} href={phone ? `tel:${phone}` : ""} />
        <ContactLink icon={MessageCircle} label="WhatsApp" value={whatsapp ? `+${whatsapp}` : ""} href={whatsapp ? `https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi, we are reviewing your NamasteNetwork event: ${event.title}`)}` : ""} />
        <ContactLink icon={Instagram} label="Instagram" value={organizer.instagram_handle || instagram} href={instagram} />
      </div>
      {!email && !phone && !whatsapp && (
        <p className="mt-2 text-xs text-amber-700">No organiser phone or email is saved yet. Ask organiser to complete Profile before enabling Namaste checkout.</p>
      )}
    </div>
  );
}

function ContactLink({ icon: Icon, label, value, href }) {
  const disabled = !value || !href;
  const content = (
    <>
      <Icon className="h-3.5 w-3.5 text-primary" />
      <span className="min-w-0">
        <span className="block text-[10px] font-black uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
        <span className="block truncate text-xs font-bold">{value || "Not provided"}</span>
      </span>
    </>
  );
  if (disabled) {
    return <div className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-2 opacity-70">{content}</div>;
  }
  return (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noreferrer" : undefined} className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-2 hover:bg-muted">
      {content}
    </a>
  );
}

const ACTIVE_BOOKING_STATUSES = new Set(["confirmed", "scanned", "partially_scanned", "partially_refunded"]);

function bookingQuantity(booking) {
  const items = asArray(booking.ticket_items);
  if (items.length) return items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  return Number(booking.quantity) || 1;
}

function bookingTicketLabel(booking) {
  const items = asArray(booking.ticket_items).filter((item) => Number(item.quantity) > 0);
  if (!items.length) return booking.category || "General";
  return items.map((item) => `${item.category || "General"} x ${Number(item.quantity) || 1}`).join(" · ");
}

function formatPurchaseTime(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function BookingsTab({ rows, events = [] }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [eventId, setEventId] = useState("all");
  const statuses = unique(rows.map((b) => b.status));
  const eventRows = rows.filter((booking) => booking.item_type === "event");
  const eventById = new Map(events.map((event) => [event.event_id, event]));
  const eventOptions = unique(eventRows.map((booking) => booking.item_id)).map((id) => ({
    id,
    title: eventById.get(id)?.title || eventRows.find((booking) => booking.item_id === id)?.item_title || id,
  })).sort((a, b) => a.title.localeCompare(b.title));
  const filtered = rows.filter((b) => (
    matchesQuery(b, query, ["user_name", "user_email", "item_title", "booking_id"])
    && (status === "All" || b.status === status)
    && (eventId === "all" || b.item_id === eventId)
  ));
  const activeFiltered = filtered.filter((booking) => ACTIVE_BOOKING_STATUSES.has(booking.status));
  const revenue = activeFiltered.reduce((sum, b) => sum + (Number(b.total) || 0) - (Number(b.refunded_amount) || 0), 0);
  const visibleTickets = activeFiltered.reduce((sum, booking) => sum + bookingQuantity(booking), 0);
  const eventSummaries = eventOptions.map((option) => {
    const event = eventById.get(option.id) || {};
    const bookings = eventRows.filter((booking) => booking.item_id === option.id);
    const active = bookings.filter((booking) => ACTIVE_BOOKING_STATUSES.has(booking.status));
    const sold = Number.isFinite(Number(event.tickets_sold))
      ? Number(event.tickets_sold)
      : active.reduce((sum, booking) => sum + bookingQuantity(booking), 0);
    const capacity = Number(event.capacity) || 0;
    const remaining = Number.isFinite(Number(event.tickets_remaining))
      ? Number(event.tickets_remaining)
      : Math.max(0, capacity - sold);
    return {
      ...option,
      bookingCount: active.length,
      sold,
      capacity,
      remaining,
      revenue: active.reduce((sum, booking) => sum + (Number(booking.total) || 0) - (Number(booking.refunded_amount) || 0), 0),
    };
  });

  return (
    <section className="space-y-4">
      <Toolbar title="Booking Ledger" count={filtered.length} query={query} setQuery={setQuery} filters={(
        <div className="flex flex-wrap gap-2">
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger className="h-9 w-[240px]" aria-label="Filter bookings by event">
              <SelectValue placeholder="All events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {eventOptions.map((event) => <SelectItem key={event.id} value={event.id}>{event.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <FilterPill options={["All", ...statuses]} value={status} onChange={setStatus} />
        </div>
      )} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Insight label="Filtered sales" value={`£${revenue.toFixed(2)}`} note="Total for visible bookings" />
        <Insight label="Tickets sold" value={visibleTickets} note="Active tickets in this view" />
        <Insight label="Refund exposure" value={`£${filtered.reduce((sum, b) => sum + (Number(b.refunded_amount) || 0), 0).toFixed(2)}`} note="Already refunded amount" />
        <Insight label="Checked in" value={filtered.filter((b) => ["scanned", "partially_scanned"].includes(b.status)).length} note="Bookings used at venue" />
      </div>
      <div className="rounded-lg border border-border">
        <div className="border-b border-border px-4 py-3">
          <h3 className="font-display text-base font-bold">Sales by event</h3>
          <p className="text-xs text-muted-foreground">Select an event to inspect its buyers and booking history.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="admin-event-booking-summary">
            <thead className="bg-muted/50 text-left">
              <tr><th className="p-3">Event</th><th className="p-3">Bookings</th><th className="p-3">Sold</th><th className="p-3">Capacity</th><th className="p-3">Remaining</th><th className="p-3">Revenue</th><th className="p-3 text-right">View</th></tr>
            </thead>
            <tbody>
              {eventSummaries.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No event bookings yet.</td></tr>
              ) : eventSummaries.map((event) => (
                <tr key={event.id} className="border-t border-border">
                  <td className="p-3 font-bold">{event.title}</td>
                  <td className="p-3">{event.bookingCount}</td>
                  <td className="p-3 font-bold">{event.sold}</td>
                  <td className="p-3">{event.capacity || "Unlimited"}</td>
                  <td className="p-3 font-bold text-primary">{event.capacity ? event.remaining : "Unlimited"}</td>
                  <td className="p-3">£{event.revenue.toFixed(2)}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => setEventId(event.id)}>Buyers</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DataTable>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Booking</TableHead>
              <TableHead>Tickets</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead>Purchased</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((b) => (
              <TableRow key={b.booking_id}>
                <TableCell>
                  <p className="font-bold">{b.user_name || "Unknown user"}</p>
                  <p className="text-xs text-muted-foreground">{b.user_email}</p>
                </TableCell>
                <TableCell>
                  <p className="font-bold">{b.item_title}</p>
                  <p className="text-xs text-muted-foreground">{b.item_type}</p>
                </TableCell>
                <TableCell className="max-w-[260px] text-xs">{bookingTicketLabel(b)}</TableCell>
                <TableCell className="font-bold">{bookingQuantity(b)}</TableCell>
                <TableCell className="whitespace-nowrap text-xs">{formatPurchaseTime(b.created_at)}</TableCell>
                <TableCell className="font-bold">£{money(b.total)}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => copyText(b.booking_id, "Booking ID copied")}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataTable>
    </section>
  );
}

function UsersTab({ rows }) {
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("All");
  const roles = unique(rows.map((u) => u.role || "user"));
  const filtered = rows.filter((u) => matchesQuery(u, query, ["name", "email", "role"]) && (role === "All" || (u.role || "user") === role));
  const payoutReady = rows.filter((u) => u.role === "organizer" && u.stripe_account_id && u.stripe_charges_enabled && u.stripe_payouts_enabled).length;
  const payoutPending = rows.filter((u) => u.role === "organizer" && u.stripe_account_id && !(u.stripe_charges_enabled && u.stripe_payouts_enabled)).length;

  return (
    <section className="space-y-4">
      <Toolbar title="User Directory" count={filtered.length} query={query} setQuery={setQuery} filters={(
        <FilterPill options={["All", ...roles]} value={role} onChange={setRole} icon={UserCog} />
      )} />
      <div className="grid gap-3 md:grid-cols-4">
        {roles.map((r) => <Insight key={r} label={r} value={rows.filter((u) => (u.role || "user") === r).length} note="Accounts" />)}
        <Insight label="Payout ready" value={payoutReady} note="Organisers with live Stripe payouts" />
        <Insight label="Payout pending" value={payoutPending} note="Connect started, not ready" />
      </div>
      <DataTable>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Auth</TableHead>
              <TableHead>Payouts</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.user_id}>
                <TableCell className="font-bold">{u.name || "Unnamed"}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="outline">{u.role || "user"}</Badge></TableCell>
                <TableCell>{u.local_auth ? "Local" : "Google"}</TableCell>
                <TableCell>
                  <PayoutStatus user={u} />
                </TableCell>
                <TableCell className="text-xs">{formatDate(u.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataTable>
    </section>
  );
}

function OrganizersTab({ rows, reload }) {
  const [query, setQuery] = useState("");
  const filtered = rows.filter((row) => matchesQuery(row.organizer || {}, query, ["name", "email", "mobile", "user_id"]));
  const totals = filtered.reduce((acc, row) => ({
    gross: acc.gross + Number(row.totals?.gross_pence || 0),
    fees: acc.fees + Number(row.totals?.platform_fee_pence || 0),
    due: acc.due + Number(row.totals?.organizer_due_pence || 0),
    manual: acc.manual + Number(row.totals?.manual_due_pence || 0),
  }), { gross: 0, fees: 0, due: 0, manual: 0 });

  return (
    <section className="space-y-4">
      <Toolbar title="Organiser Finance" count={filtered.length} query={query} setQuery={setQuery} />
      <div className="grid gap-3 md:grid-cols-4">
        <Insight label="Gross sales" value={moneyFromPence(totals.gross)} note="Completed ticket sales" />
        <Insight label="Namaste fees" value={moneyFromPence(totals.fees)} note="After recorded refunds" />
        <Insight label="Organiser share" value={moneyFromPence(totals.due)} note="Automatic and manual" />
        <Insight label="Manual due" value={moneyFromPence(totals.manual)} note="Not routed by Connect" />
      </div>
      <DataTable>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organiser</TableHead>
              <TableHead>Events</TableHead>
              <TableHead>Gross</TableHead>
              <TableHead>Booking fees</TableHead>
              <TableHead>Organiser share</TableHead>
              <TableHead>Payout route</TableHead>
              <TableHead>Buyer fee</TableHead>
              <TableHead className="w-[90px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => <OrganizerFinanceRow key={row.organizer.user_id} row={row} reload={reload} />)}
          </TableBody>
        </Table>
      </DataTable>
    </section>
  );
}

function OrganizerFinanceRow({ row, reload }) {
  const organizer = row.organizer;
  const [expanded, setExpanded] = useState(false);
  const [percentage, setPercentage] = useState(String(organizer.platform_fee_pct ?? 5));
  const [fixedPounds, setFixedPounds] = useState((Number(organizer.platform_fee_fixed_pence ?? 50) / 100).toFixed(2));
  const [paymentMode, setPaymentMode] = useState(organizer.ticketing_payment_mode || "disabled");
  const [saving, setSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);

  const saveFee = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/organizers/${organizer.user_id}/fee`, {
        percentage: Number(percentage),
        fixed_pence: Math.round(Number(fixedPounds || 0) * 100),
      });
      toast.success("Buyer booking fee updated for future sales");
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update organiser fee");
    } finally {
      setSaving(false);
    }
  };

  const savePaymentMode = async () => {
    setPaymentSaving(true);
    try {
      await api.put(`/admin/organizers/${organizer.user_id}/payment-mode`, { mode: paymentMode });
      toast.success(`${paymentModeLabels[paymentMode]} selected for future ticket sales`);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update organiser payment access");
    } finally {
      setPaymentSaving(false);
    }
  };

  const ready = organizer.stripe_charges_enabled && organizer.stripe_payouts_enabled;
  const routeReady = paymentMode === "platform" || (paymentMode === "stripe_connect" && ready);
  return (
    <>
      <TableRow>
        <TableCell>
          <p className="font-bold">{organizer.name || "Unnamed organiser"}</p>
          <p className="text-xs text-muted-foreground">{organizer.email}</p>
        </TableCell>
        <TableCell>{row.totals?.events || 0}</TableCell>
        <TableCell>{moneyFromPence(row.totals?.gross_pence)}</TableCell>
        <TableCell>{moneyFromPence(row.totals?.platform_fee_pence)}</TableCell>
        <TableCell>{moneyFromPence(row.totals?.organizer_due_pence)}</TableCell>
        <TableCell>
          <div className="min-w-[210px] space-y-2">
            <Badge className={routeReady ? statusStyles.active : paymentMode === "disabled" ? statusStyles.inactive : statusStyles.pending}>
              {paymentModeLabels[paymentMode] || paymentModeLabels.disabled}
            </Badge>
            <Select value={paymentMode} onValueChange={setPaymentMode}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="disabled">Disabled</SelectItem>
                <SelectItem value="platform">Namaste collection</SelectItem>
                <SelectItem value="stripe_connect">Stripe Express</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={savePaymentMode} disabled={paymentSaving}>
              {paymentSaving ? "Saving" : "Apply route"}
            </Button>
            {paymentMode === "stripe_connect" && !ready && (
              <p className="text-[11px] text-amber-700">Checkout stays blocked until Express onboarding and payouts are active.</p>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex min-w-[230px] items-center gap-2">
            <Input aria-label="Buyer booking fee percentage" type="number" min="0" max="100" step="0.1" value={percentage} onChange={(e) => setPercentage(e.target.value)} className="h-8 w-20" />
            <span className="text-xs text-muted-foreground">% + £</span>
            <Input aria-label="Fixed fee per order" type="number" min="0" step="0.01" value={fixedPounds} onChange={(e) => setFixedPounds(e.target.value)} className="h-8 w-20" />
            <Button size="sm" onClick={saveFee} disabled={saving}>{saving ? "Saving" : "Save"}</Button>
          </div>
        </TableCell>
        <TableCell>
          <Button size="sm" variant="outline" onClick={() => setExpanded((value) => !value)}>
            {expanded ? "Hide" : "Events"}
          </Button>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/25 p-4">
            <OrganizerEventFees events={row.events || []} organizer={organizer} reload={reload} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function OrganizerEventFees({ events, organizer, reload }) {
  const saveOverride = async (event, percentage, fixedPounds) => {
    const inherited = percentage === "";
    try {
      await api.put(`/admin/events/${event.event_id}/fee-override`, {
        percentage: inherited ? null : Number(percentage),
        fixed_pence: inherited ? null : Math.round(Number(fixedPounds || 0) * 100),
      });
      toast.success(inherited ? "Event now inherits organiser pricing" : "Event fee override saved");
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update event fee");
    }
  };
  if (!events.length) return <EmptyLine text="No events belong to this organiser yet." />;
  return (
    <div className="space-y-2">
      <p className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">Event fee overrides</p>
      {events.map((event) => (
        <EventFeeEditor key={event.event_id} event={event} organizer={organizer} onSave={saveOverride} />
      ))}
    </div>
  );
}

function EventFeeEditor({ event, organizer, onSave }) {
  const hasOverride = event.platform_fee_pct_override != null;
  const [percentage, setPercentage] = useState(hasOverride ? String(event.platform_fee_pct_override) : "");
  const [fixedPounds, setFixedPounds] = useState(hasOverride ? (Number(event.platform_fee_fixed_pence_override || 0) / 100).toFixed(2) : "");
  return (
    <div className="grid items-center gap-2 border-t border-border py-2 md:grid-cols-[minmax(220px,1fr)_110px_110px_auto]">
      <div>
        <p className="text-sm font-bold">{event.title}</p>
        <p className="text-xs text-muted-foreground">{event.date || "No date"} · {hasOverride ? "Event override" : `Inherits ${organizer.platform_fee_pct}% + ${moneyFromPence(organizer.platform_fee_fixed_pence)}`}</p>
      </div>
      <Input aria-label={`${event.title} fee percentage`} type="number" min="0" max="100" step="0.1" placeholder={`${organizer.platform_fee_pct}%`} value={percentage} onChange={(e) => setPercentage(e.target.value)} />
      <Input aria-label={`${event.title} fixed fee`} type="number" min="0" step="0.01" placeholder="Fixed £" value={fixedPounds} onChange={(e) => setFixedPounds(e.target.value)} />
      <Button size="sm" variant="outline" onClick={() => onSave(event, percentage, fixedPounds)}>{percentage === "" ? "Use organiser fee" : "Save override"}</Button>
    </div>
  );
}

function PayoutStatus({ user }) {
  if (user.role !== "organizer") {
    return <span className="text-xs text-muted-foreground">Platform</span>;
  }
  const ready = Boolean(user.stripe_account_id && user.stripe_charges_enabled && user.stripe_payouts_enabled);
  const status = user.stripe_account_id
    ? ready ? "ready" : (user.stripe_account_status || "pending")
    : "not connected";
  return (
    <div className="space-y-1">
      <Badge className={ready ? statusStyles.active : statusStyles.pending}>
        <CreditCard className="mr-1 h-3 w-3" />
        {status}
      </Badge>
      {user.stripe_account_id && (
        <button
          type="button"
          onClick={() => copyText(user.stripe_account_id, "Stripe account ID copied")}
          className="block max-w-[180px] truncate text-left font-mono text-[11px] text-muted-foreground hover:text-primary"
        >
          {user.stripe_account_id}
        </button>
      )}
    </div>
  );
}

function CreatorsAdminTab({ rows, reload }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const filtered = rows.filter((u) => {
    const visible = Boolean(u.public_profile && u.creator_opt_in);
    const state = visible ? "Live" : u.creator_opt_in ? "Hidden" : "Disabled";
    return matchesQuery(u, query, ["name", "email", "display_name", "instagram_handle", "bio"])
      && (status === "All" || state === status);
  });

  const updateCreator = async (userId, payload, message) => {
    await api.put(`/admin/creators/${userId}`, payload);
    toast.success(message);
    reload();
  };

  return (
    <section className="space-y-4">
      <Toolbar title="Creator Profiles" count={filtered.length} query={query} setQuery={setQuery} filters={(
        <FilterPill options={["All", "Live", "Hidden", "Disabled"]} value={status} onChange={setStatus} icon={Sparkles} />
      )} />
      <div className="grid gap-3 md:grid-cols-4">
        <Insight label="Live" value={rows.filter((u) => u.public_profile && u.creator_opt_in).length} note="Visible in directory" />
        <Insight label="Hidden" value={rows.filter((u) => u.creator_opt_in && !u.public_profile).length} note="Opted in but not public" />
        <Insight label="Featured" value={rows.filter((u) => u.featured_creator).length} note="Sorted first" />
        <Insight label="Disabled" value={rows.filter((u) => !u.creator_opt_in).length} note="Not discoverable" />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {filtered.map((u) => {
          const live = Boolean(u.public_profile && u.creator_opt_in);
          const categories = u.creator_categories || [];
          const languages = u.creator_languages || u.language_preferences || [];
          const locations = u.creator_locations || u.city_preferences || [];
          return (
            <article key={u.user_id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold">{u.display_name || u.name || "Unnamed creator"}</p>
                    <StatusBadge status={live ? "active" : u.creator_opt_in ? "pending" : "inactive"} />
                    {u.featured_creator && <Badge>Featured</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                  {u.instagram_handle && <p className="text-xs font-bold text-primary">{u.instagram_handle}</p>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => copyText(u.user_id, "Creator user ID copied")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <EditableText label="Display name" value={u.display_name || ""} onSave={(value) => updateCreator(u.user_id, { display_name: value }, "Display name updated")} />
                <EditableText label="Instagram" value={u.instagram_handle || ""} onSave={(value) => updateCreator(u.user_id, { instagram_handle: value }, "Instagram handle updated")} />
                <EditableText label="Categories" value={categories.join(", ")} onSave={(value) => updateCreator(u.user_id, { creator_categories: csv(value) }, "Categories updated")} />
                <EditableText label="Languages" value={languages.join(", ")} onSave={(value) => updateCreator(u.user_id, { creator_languages: csv(value) }, "Languages updated")} />
                <EditableText label="Locations" value={locations.join(", ")} onSave={(value) => updateCreator(u.user_id, { creator_locations: csv(value) }, "Locations updated")} />
                <EditableText label="Interests" value={(u.creator_interests || []).join(", ")} onSave={(value) => updateCreator(u.user_id, { creator_interests: csv(value) }, "Interests updated")} />
              </div>
              <div className="mt-3">
                <EditableText area label="Bio" value={u.bio || ""} onSave={(value) => updateCreator(u.user_id, { bio: value }, "Bio updated")} />
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => updateCreator(u.user_id, { featured_creator: !u.featured_creator }, u.featured_creator ? "Creator unfeatured" : "Creator featured")}>
                  {u.featured_creator ? "Unfeature" : "Feature"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateCreator(u.user_id, { creator_opt_in: true, public_profile: true }, "Creator enabled")}>
                  Enable public
                </Button>
                <Button size="sm" variant="outline" onClick={() => updateCreator(u.user_id, { public_profile: false }, "Creator hidden")}>
                  Hide
                </Button>
                <Button size="sm" variant="destructive" onClick={() => updateCreator(u.user_id, { creator_opt_in: false, public_profile: false, featured_creator: false }, "Creator disabled")}>
                  Disable
                </Button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function EditableText({ label, value, onSave, area = false }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => setDraft(value || ""), [value]);
  const changed = draft !== (value || "");
  return (
    <label className="block">
      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      {area ? (
        <Textarea className="mt-1 min-h-20" value={draft} onChange={(e) => setDraft(e.target.value)} />
      ) : (
        <Input className="mt-1" value={draft} onChange={(e) => setDraft(e.target.value)} />
      )}
      {changed && (
        <Button size="sm" className="mt-2 rounded-full" onClick={() => onSave(draft)}>
          Save {label}
        </Button>
      )}
    </label>
  );
}

const csv = (value) => String(value || "").split(",").map((part) => part.trim()).filter(Boolean);

function AdsTab({ rows, reload }) {
  const [query, setQuery] = useState("");
  const filtered = rows.filter((a) => matchesQuery({ ...a, title: a.event?.title }, query, ["title", "format", "ad_id"]));

  return (
    <section className="space-y-4">
      <Toolbar title="Featured Ads" count={filtered.length} query={query} setQuery={setQuery} />
      <div className="grid gap-4 lg:grid-cols-2">
        {filtered.map((a) => {
          const ctr = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(2) : "0.00";
          return (
            <article key={a.ad_id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold">{a.event?.title || a.event_id}</p>
                  <p className="text-xs text-muted-foreground">{a.format} · {a.languages?.join(", ") || "All languages"}</p>
                </div>
                <StatusBadge status={a.active ? "active" : "inactive"} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Insight label="Views" value={a.impressions || 0} note="Impressions" />
                <Insight label="Clicks" value={a.clicks || 0} note="Traffic" />
                <Insight label="CTR" value={`${ctr}%`} note="Conversion" />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={async () => { await api.put(`/admin/ads/${a.ad_id}`, { active: !a.active }); toast.success(a.active ? "Ad disabled" : "Ad enabled"); reload(); }}>
                  {a.active ? "Disable" : "Enable"}
                </Button>
                <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/admin/ads/${a.ad_id}`); toast.success("Ad deleted"); reload(); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function CategoriesTab({ rows, reload }) {
  const [n, setN] = useState({ name: "", icon: "Sparkles", color: "#F59E0B", active: true });
  const toggleCategory = async (category) => {
    await api.put(`/categories/${category.category_id}`, { active: category.active === false });
    toast.success(`${category.name} ${category.active === false ? "enabled" : "disabled"}`);
    reload();
  };

  return (
    <section className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <div className="rounded-lg border border-border bg-card p-5">
        <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Create Category</p>
        <div className="mt-4 space-y-3">
          <Input placeholder="Name" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="cat-name" />
          <Input placeholder="Icon name" value={n.icon} onChange={(e) => setN({ ...n, icon: e.target.value })} />
          <div className="flex gap-2">
            <Input placeholder="Color hex" value={n.color} onChange={(e) => setN({ ...n, color: e.target.value })} />
            <span className="h-10 w-10 rounded-md border border-border" style={{ backgroundColor: n.color }} />
          </div>
          <Button onClick={async () => { await api.post("/categories", n); setN({ ...n, name: "" }); toast.success("Category added"); reload(); }} className="w-full rounded-full">
            <Plus className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((c) => {
          const active = c.active !== false;
          return (
          <div key={c.category_id} className={`rounded-lg border bg-card p-4 ${active ? "border-border" : "border-dashed border-muted-foreground/40 opacity-75"}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full" style={{ backgroundColor: c.color }} />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold">{c.name}</p>
                    <StatusBadge status={active ? "active" : "inactive"} />
                  </div>
                  <p className="text-xs text-muted-foreground">{c.icon} · order {c.order}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => toggleCategory(c)} className="rounded-full">
                  {active ? "Disable" : "Enable"}
                </Button>
                <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/categories/${c.category_id}`); toast.success("Category deleted"); reload(); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        );})}
      </div>
    </section>
  );
}

const slugifyCampaign = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

function CampaignsTab({ rows, reload }) {
  const campaigns = asArray(rows);
  const [draft, setDraft] = useState({ key: "", label: "", route: "", description: "", active: true });

  const saveRows = async (nextRows, message) => {
    await api.put("/admin/app-options/event_campaigns", { value: nextRows });
    toast.success(message);
    reload();
  };

  const addCampaign = async () => {
    const label = draft.label.trim();
    const key = slugifyCampaign(draft.key || label);
    if (!label || !key) {
      toast.error("Campaign label is required.");
      return;
    }
    if (campaigns.some((item) => item.key === key)) {
      toast.error("Campaign key already exists.");
      return;
    }
    await saveRows([
      ...campaigns,
      {
        key,
        label,
        active: draft.active !== false,
        route: draft.route.trim(),
        description: draft.description.trim(),
      },
    ], "Campaign added");
    setDraft({ key: "", label: "", route: "", description: "", active: true });
  };

  const updateCampaign = async (key, patch) => {
    await saveRows(campaigns.map((item) => item.key === key ? { ...item, ...patch } : item), "Campaign updated");
  };

  const deleteCampaign = async (key) => {
    await saveRows(campaigns.filter((item) => item.key !== key), "Campaign deleted");
  };

  return (
    <section className="grid gap-4 lg:grid-cols-[380px_1fr]">
      <div className="rounded-lg border border-border bg-card p-5">
        <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Create Campaign</p>
        <p className="mt-2 text-sm text-muted-foreground">Campaigns group events across categories, for example Navratri, Diwali, Holi or Bollywood Nights.</p>
        <div className="mt-4 space-y-3">
          <Input placeholder="Label, e.g. Navratri 2026" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
          <Input placeholder="Key, e.g. navratri-2026" value={draft.key} onChange={(e) => setDraft({ ...draft, key: slugifyCampaign(e.target.value) })} />
          <Input placeholder="Route, e.g. /navratri" value={draft.route} onChange={(e) => setDraft({ ...draft, route: e.target.value })} />
          <Textarea placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          <Button onClick={addCampaign} className="w-full rounded-full">
            <Plus className="mr-2 h-4 w-4" />
            Add Campaign
          </Button>
        </div>
      </div>
      <div className="grid gap-3 xl:grid-cols-2">
        {campaigns.map((campaign) => {
          const active = campaign.active !== false;
          return (
            <div key={campaign.key} className={`rounded-lg border bg-card p-4 ${active ? "border-border" : "border-dashed border-muted-foreground/40 opacity-75"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold">{campaign.label}</p>
                    <StatusBadge status={active ? "active" : "inactive"} />
                  </div>
                  <p className="mt-1 text-xs font-mono text-muted-foreground">{campaign.key}</p>
                  {campaign.description && <p className="mt-2 text-sm text-muted-foreground">{campaign.description}</p>}
                  {campaign.route && <p className="mt-2 text-xs font-bold text-primary">{campaign.route}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => updateCampaign(campaign.key, { active: !active })} className="rounded-full">
                    {active ? "Disable" : "Enable"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteCampaign(campaign.key)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <EditableText label="Label" value={campaign.label || ""} onSave={(value) => updateCampaign(campaign.key, { label: value })} />
                <EditableText label="Route" value={campaign.route || ""} onSave={(value) => updateCampaign(campaign.key, { route: value })} />
                <div className="md:col-span-2">
                  <EditableText label="Description" value={campaign.description || ""} onSave={(value) => updateCampaign(campaign.key, { description: value })} area />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LanguagesTab({ rows, reload }) {
  const [n, setN] = useState({ name: "", code: "" });

  return (
    <section className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <div className="rounded-lg border border-border bg-card p-5">
        <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Language Coverage</p>
        <div className="mt-4 space-y-3">
          <Input placeholder="Name" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} />
          <Input placeholder="Code" value={n.code} onChange={(e) => setN({ ...n, code: e.target.value })} />
          <Button onClick={async () => { await api.post("/languages", n); setN({ name: "", code: "" }); toast.success("Language added"); reload(); }} className="w-full rounded-full">
            <Plus className="mr-2 h-4 w-4" />
            Add Language
          </Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((l) => (
          <div key={l.language_id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-bold">{l.name}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{l.code}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/languages/${l.language_id}`); toast.success("Language deleted"); reload(); }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CitiesTab({ rows, reload }) {
  const [name, setName] = useState("");

  return (
    <section className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <div className="rounded-lg border border-border bg-card p-5">
        <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">City Coverage</p>
        <p className="mt-2 text-sm text-muted-foreground">Cities added here appear in profile preferences, create-event forms and city filters.</p>
        <div className="mt-4 space-y-3">
          <Input placeholder="City name" value={name} onChange={(e) => setName(e.target.value)} />
          <Button
            onClick={async () => {
              await api.post("/cities", { name });
              setName("");
              toast.success("City added");
              reload();
            }}
            className="w-full rounded-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add City
          </Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((c) => (
          <div key={c.city_id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-bold">{c.name}</p>
                <p className="text-xs text-muted-foreground">Order {c.order}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/cities/${c.city_id}`); toast.success("City deleted"); reload(); }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AppOptionsTab({ options, reload }) {
  const keys = ["easy_login", "seasonal_campaign", "creator_showcase", "event_campaigns", "scanner_gates", "ticket_templates", "default_posters", "search_genres", "search_event_types", "ad_formats", "payment_methods"];
  const [drafts, setDrafts] = useState({});
  const [campaign, setCampaign] = useState({
    enabled: true,
    key: "navratri-2026",
    label: "Navratri 2026",
    nav_label: "Navratri",
    title: "Garba, Dandiya and Raas nights",
    subtitle: "Find verified UK Navratri events, tickets and external listings.",
    route: "/navratri",
    icon: "sparkles",
    colors: ["#F84464", "#7c2d12"],
  });
  const [creatorShowcase, setCreatorShowcase] = useState({
    enabled: true,
    eyebrow: "Community voices",
    title: "Featured creators",
    subtitle: "Influencers, food bloggers, promoters and community pages recommending UK Indian events.",
    limit: 8,
  });

  useEffect(() => {
    const next = {};
    keys.forEach((key) => {
      next[key] = JSON.stringify(options?.[key] ?? null, null, 2);
    });
    setDrafts(next);
    if (options?.seasonal_campaign) {
      setCampaign((current) => ({ ...current, ...options.seasonal_campaign }));
    }
    if (options?.creator_showcase) {
      setCreatorShowcase((current) => ({ ...current, ...options.creator_showcase }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(options || {})]);

  const save = async (key) => {
    try {
      const value = JSON.parse(drafts[key]);
      await api.put(`/admin/app-options/${key}`, { value });
      toast.success(`${key.replaceAll("_", " ")} updated`);
      reload();
    } catch (e) {
      toast.error(e instanceof SyntaxError ? "Invalid JSON" : "Failed to update option");
    }
  };

  const saveCampaign = async () => {
    const value = {
      ...campaign,
      colors: Array.isArray(campaign.colors) && campaign.colors.length >= 2
        ? campaign.colors.slice(0, 2)
        : ["#F84464", "#7c2d12"],
    };
    await api.put("/admin/app-options/seasonal_campaign", { value });
    setDrafts((current) => ({ ...current, seasonal_campaign: JSON.stringify(value, null, 2) }));
    toast.success("Seasonal campaign updated");
    reload();
  };

  const saveCreatorShowcase = async () => {
    const value = {
      ...creatorShowcase,
      limit: Number(creatorShowcase.limit || 8),
    };
    await api.put("/admin/app-options/creator_showcase", { value });
    setDrafts((current) => ({ ...current, creator_showcase: JSON.stringify(value, null, 2) }));
    toast.success("Creator showcase updated");
    reload();
  };

  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-primary/25 bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Seasonal Campaign</p>
            <h3 className="mt-1 font-display text-2xl font-black">Top mobile festival section</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Controls the replaceable Navratri/festival card on mobile and the seasonal links on web.
            </p>
          </div>
          <Button variant={campaign.enabled === false ? "outline" : "default"} onClick={() => setCampaign((current) => ({ ...current, enabled: current.enabled === false }))} className="rounded-full">
            {campaign.enabled === false ? "Disabled" : "Enabled"}
          </Button>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <Input value={campaign.label || ""} onChange={(e) => setCampaign({ ...campaign, label: e.target.value })} placeholder="Badge label, e.g. Navratri 2026" />
          <Input value={campaign.nav_label || ""} onChange={(e) => setCampaign({ ...campaign, nav_label: e.target.value })} placeholder="Short nav label, e.g. Navratri" />
          <Input value={campaign.title || ""} onChange={(e) => setCampaign({ ...campaign, title: e.target.value })} placeholder="Title" />
          <Input value={campaign.route || ""} onChange={(e) => setCampaign({ ...campaign, route: e.target.value })} placeholder="Route, e.g. /navratri" />
          <Input value={campaign.colors?.[0] || ""} onChange={(e) => setCampaign({ ...campaign, colors: [e.target.value, campaign.colors?.[1] || "#7c2d12"] })} placeholder="Start colour" />
          <Input value={campaign.colors?.[1] || ""} onChange={(e) => setCampaign({ ...campaign, colors: [campaign.colors?.[0] || "#F84464", e.target.value] })} placeholder="End colour" />
        </div>
        <Textarea value={campaign.subtitle || ""} onChange={(e) => setCampaign({ ...campaign, subtitle: e.target.value })} className="mt-3" placeholder="Subtitle" />
        <div className="mt-4 flex justify-end">
          <Button onClick={saveCampaign} className="rounded-full">
            <Sparkles className="mr-2 h-4 w-4" />
            Save Seasonal Campaign
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-primary/25 bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Community voices</p>
            <h3 className="mt-1 font-display text-2xl font-black">Featured creators section</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Controls the homepage creator rail on web and mobile. Individual creators are still managed from the Creators tab.
            </p>
          </div>
          <Button
            variant={creatorShowcase.enabled === false ? "outline" : "default"}
            onClick={() => setCreatorShowcase((current) => ({ ...current, enabled: current.enabled === false }))}
            className="rounded-full"
          >
            {creatorShowcase.enabled === false ? "Disabled" : "Enabled"}
          </Button>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <Input value={creatorShowcase.eyebrow || ""} onChange={(e) => setCreatorShowcase({ ...creatorShowcase, eyebrow: e.target.value })} placeholder="Eyebrow, e.g. Community voices" />
          <Input value={creatorShowcase.title || ""} onChange={(e) => setCreatorShowcase({ ...creatorShowcase, title: e.target.value })} placeholder="Title, e.g. Featured creators" />
          <Input type="number" min="1" max="20" value={creatorShowcase.limit || 8} onChange={(e) => setCreatorShowcase({ ...creatorShowcase, limit: e.target.value })} placeholder="Creator limit" />
        </div>
        <Textarea value={creatorShowcase.subtitle || ""} onChange={(e) => setCreatorShowcase({ ...creatorShowcase, subtitle: e.target.value })} className="mt-3" placeholder="Subtitle" />
        <div className="mt-4 flex justify-end">
          <Button onClick={saveCreatorShowcase} className="rounded-full">
            <Sparkles className="mr-2 h-4 w-4" />
            Save Creator Showcase
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {keys.map((key) => (
        <div key={key} className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">{key.replaceAll("_", " ")}</p>
              <p className="mt-1 text-xs text-muted-foreground">Stored as JSON and consumed by web and Expo.</p>
            </div>
            <Button size="sm" onClick={() => save(key)} className="rounded-full">Save</Button>
          </div>
          <Textarea
            value={drafts[key] || ""}
            onChange={(e) => setDrafts((current) => ({ ...current, [key]: e.target.value }))}
            className="mt-4 min-h-[180px] font-mono text-xs"
          />
        </div>
        ))}
      </div>
    </section>
  );
}

function SettingsTab({ settings, users, languages = [], cities = [], reload }) {
  const [pin, setPin] = useState("");
  const [bcast, setBcast] = useState({ title: "", body: "", audience: "all", languages: [], cities: [], route: "" });
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [audiencePreviewLoading, setAudiencePreviewLoading] = useState(false);
  const current = settings?.global_scanner_pin || "";
  const languageOptions = [...new Set([
    ...languages.map((language) => language.name),
    ...users.flatMap((user) => user.language_preferences || [user.ad_language_pref]),
  ].filter((value) => value && value !== "All"))].sort();
  const cityOptions = [...new Set([
    ...cities.map((city) => city.name),
    ...users.flatMap((user) => user.city_preferences || []),
  ].filter((value) => value && value !== "All UK"))].sort();
  const audienceSelectionComplete = !(
    (["language", "language_city"].includes(bcast.audience) && !bcast.languages.length)
    || (["city", "language_city"].includes(bcast.audience) && !bcast.cities.length)
  );

  useEffect(() => {
    if (!audienceSelectionComplete) {
      setAudiencePreview(null);
      setAudiencePreviewLoading(false);
      return undefined;
    }
    let active = true;
    setAudiencePreviewLoading(true);
    const timer = setTimeout(() => {
      api.post("/admin/notifications/broadcast/preview", {
        audience: bcast.audience,
        languages: bcast.languages,
        cities: bcast.cities,
      }).then((response) => {
        if (active) setAudiencePreview(response.data);
      }).catch(() => {
        if (active) setAudiencePreview(null);
      }).finally(() => {
        if (active) setAudiencePreviewLoading(false);
      });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [bcast.audience, bcast.languages, bcast.cities, audienceSelectionComplete]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-lg border border-border bg-card p-5">
        <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-primary">
          <Settings className="h-4 w-4" />
          Scanner Controls
        </p>
        <div className="mt-5 rounded-md bg-muted p-4">
          <p className="text-xs text-muted-foreground">Current global PIN</p>
          <p className="mt-1 font-mono text-2xl font-black">{current}</p>
        </div>
        <div className="mt-4 flex gap-2">
          <Input placeholder="New PIN" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="new-pin-input" />
          <Button onClick={async () => { const r = await api.put("/admin/settings/scanner-pin", { global_scanner_pin: pin }); setPin(""); toast.success(`PIN updated to ${r.data.global_scanner_pin}`); reload(); }} className="rounded-full">
            Update
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-primary">
          <Bell className="h-4 w-4" />
          Push Broadcast
        </p>
        <p className="mt-2 text-sm text-muted-foreground">Send an in-app notification to everyone or a selected audience.</p>
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {[['all', 'All users'], ['language', 'Language'], ['city', 'Location'], ['language_city', 'Language + location']].map(([value, label]) => (
              <Button key={value} type="button" size="sm" variant={bcast.audience === value ? "default" : "outline"} onClick={() => setBcast({ ...bcast, audience: value, languages: [], cities: [] })} className="rounded-full">
                {label}
              </Button>
            ))}
          </div>
          {["language", "language_city"].includes(bcast.audience) && (
            <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={bcast.languages[0] || ""} onChange={(e) => setBcast({ ...bcast, languages: e.target.value ? [e.target.value] : [] })}>
              <option value="">Select language</option>
              {languageOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          )}
          {["city", "language_city"].includes(bcast.audience) && (
            <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={bcast.cities[0] || ""} onChange={(e) => setBcast({ ...bcast, cities: e.target.value ? [e.target.value] : [] })}>
              <option value="">Select location</option>
              {cityOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          )}
          <div className="rounded-md border border-border bg-muted/40 px-4 py-3" aria-live="polite">
            {audiencePreviewLoading ? (
              <p className="text-sm text-muted-foreground">Calculating audience...</p>
            ) : audiencePreview ? (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <p><span className="font-black text-foreground">{audiencePreview.matched_users}</span> users will receive the inbox notification</p>
                <p><span className="font-black text-foreground">{audiencePreview.registered_devices}</span> registered devices can receive real-time push</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Complete the audience selection to calculate recipients.</p>
            )}
          </div>
          <Input placeholder="Title" value={bcast.title} onChange={(e) => setBcast({ ...bcast, title: e.target.value })} />
          <Textarea placeholder="Message" value={bcast.body} onChange={(e) => setBcast({ ...bcast, body: e.target.value })} />
          <Input placeholder="Optional app route, e.g. /event/evt_123" value={bcast.route} onChange={(e) => setBcast({ ...bcast, route: e.target.value })} />
          {bcast.route && !bcast.route.trim().startsWith("/") && <p className="text-xs text-destructive">Route must start with /, for example /event/evt_123.</p>}
          <Button disabled={!bcast.title.trim() || !bcast.body.trim() || Boolean(bcast.route && !bcast.route.trim().startsWith("/")) || !audienceSelectionComplete} onClick={async () => { const r = await api.post("/admin/notifications/broadcast", { ...bcast, route: bcast.route.trim() }); toast.success(`Added to ${r.data.sent} user inboxes; ${r.data.push_accepted || 0} device alerts accepted`); setBcast({ title: "", body: "", audience: "all", languages: [], cities: [], route: "" }); }} className="rounded-full" data-testid="broadcast-btn">
            Broadcast
          </Button>
          <p className="text-xs text-muted-foreground">Language + location sends only to users matching both selections. Selected users receive an inbox message; enabled registered devices also receive the Expo Push alert.</p>
        </div>
      </section>
    </div>
  );
}

function Toolbar({ title, count, query, setQuery, filters, action }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
      <div>
        <p className="font-display text-xl font-black">{title}</p>
        <p className="text-xs text-muted-foreground">{count} records visible</p>
      </div>
      <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search records" className="pl-9" />
        </div>
        {filters}
        {action}
      </div>
    </div>
  );
}

function FilterPill({ options, value, onChange, icon: Icon = SlidersHorizontal }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto rounded-full border border-border bg-muted/40 p-1">
      <Icon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${value === o ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function DataTable({ children }) {
  return <div className="overflow-hidden rounded-lg border border-border bg-card">{children}</div>;
}

function Insight({ label, value, note }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-2xl font-black">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>
    </div>
  );
}

function QuickAction({ icon: Icon, label, value, tone = "neutral" }) {
  const color = tone === "warn" ? "text-amber-700 bg-amber-500/12" : "text-primary bg-primary/10";
  return (
    <div className="flex items-center justify-between rounded-lg border border-border p-3">
      <div className="flex items-center gap-3">
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${color}`}>
          <Icon className="h-4 w-4" />
        </span>
        <p className="text-sm font-bold">{label}</p>
      </div>
      <p className="font-display text-xl font-black">{value}</p>
    </div>
  );
}

function QueueBlock({ title, rows, empty, getPrimary, getMeta }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <p className="font-bold">{title}</p>
      <div className="mt-3 space-y-3">
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : rows.map((row, idx) => (
          <div key={row.event_id || row.user_id || row.ad_id || idx} className="border-b border-border pb-2 last:border-0 last:pb-0">
            <p className="line-clamp-1 text-sm font-bold">{getPrimary(row)}</p>
            <p className="text-xs text-muted-foreground">{getMeta(row)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={statusStyles[status] || "bg-muted text-muted-foreground border-border"}>
      {String(status || "unknown").replace("_", " ")}
    </Badge>
  );
}

function buildMetrics(data) {
  const confirmedStatuses = new Set(["confirmed", "scanned", "partially_refunded"]);
  const confirmedBookings = data.bookings.filter((b) => confirmedStatuses.has(b.status)).length;
  const scannedBookings = data.bookings.filter((b) => b.status === "scanned").length;
  const revenue = data.bookings
    .filter((b) => confirmedStatuses.has(b.status))
    .reduce((sum, b) => sum + (Number(b.total) || 0) - (Number(b.refunded_amount) || 0), 0);
  return {
    totalListings: data.movies.length + data.events.length,
    activeAds: data.ads.filter((a) => a.active).length,
    totalAdClicks: data.ads.reduce((sum, a) => sum + (Number(a.clicks) || 0), 0),
    totalAdImpressions: data.ads.reduce((sum, a) => sum + (Number(a.impressions) || 0), 0),
    revenue,
    confirmedBookings,
    scannedBookings,
    admins: data.users.filter((u) => u.role === "admin").length,
    organisers: data.users.filter((u) => u.role === "organizer").length,
    capacity: data.events.reduce((sum, e) => sum + (Number(e.capacity) || 0), 0),
    ticketsSold: data.events.reduce((sum, e) => sum + (Number(e.tickets_sold) || 0), 0),
  };
}

function unique(values) {
  return [...new Set(values.filter(Boolean))].sort();
}

function matchesQuery(row, query, keys) {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return keys.some((key) => String(row[key] || "").toLowerCase().includes(q));
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function formatDate(value) {
  if (!value) return "Unknown";
  return String(value).slice(0, 10);
}

function moneyFromPence(value) {
  return `£${(Number(value || 0) / 100).toFixed(2)}`;
}

function formatEventSchedule(event) {
  if (event.end_date) {
    return `${event.date} to ${event.end_date}`;
  }
  return event.date || "Date TBC";
}

function copyText(value, message) {
  navigator.clipboard?.writeText(value);
  toast.success(message);
}
